/**
 * Componente principal do Modal de Abrir Nova OS
 * 
 * Este componente encapsula toda a funcionalidade do modal de criação de OS,
 * usando os serviços, hooks e utils da feature new-os.
 * 
 * NOTA: Este componente mantém toda a estrutura visual existente para garantir
 * zero regressões visuais durante a migração.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import EquipmentStep from './EquipmentStep';
import EquipmentInfoStep from './EquipmentInfoStep';
import EquipmentSelectionStep from './EquipmentSelectionStep';
import LaudoStep from './LaudoStep';
import { 
  getLastOS
} from '../services/equipmentService';
import { 
  validateRequiredFields as validateFields,
  validateRequiredFieldsWithErrors
} from '../utils/validators';
import { loadScheduleData } from '../services/scheduleService';
import { createOS, getStatusCode, sendOSEmailSystem } from '../services/osService';
import { fetchNotificationTypes } from '../../process-config/services/emailNotificationsService';
import { getLocalDateString, getTodayLocal, getNextBusinessDay, getDayAfterNextBusinessDay, formatNumberWithMask, formatDateToPTBR, removeMask } from '../utils/formatters';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { buildHistoricoPageHtml, generateOSPdfV2Download, generateOSPdfV2FromOrder, generateOSPdfV2DownloadMultiple, generateOSPdfV2FromOrderMultiple } from '../utils/osPrintPdf';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import './OpenOSModal.css';

/**
 * Componente OpenOSModal
 * 
 * Props:
 * - isOpen: boolean - Controla se o modal está aberto
 * - onClose: function - Callback para fechar o modal
 * - API_BASE_URL: string - URL base da API (opcional, usa padrão se não fornecido)
 * - onOSCreated: function - Callback chamado quando uma OS é criada com sucesso (opcional)
 * - onViewBoardOrderDetails: function(osCodigo) - Abre modal de detalhes da OS (Board) ao clicar no número no aviso de OS aberta
 */
const OpenOSModal = ({ isOpen, onClose, API_BASE_URL: externalApiUrl, onOSCreated, onViewBoardOrderDetails }) => {
  // Função para mapear código do tipo de OS para texto
  const getTipoOsLabel = (tipoOs) => {
    const tipos = {
      'N': 'Corretiva',
      'C': 'Corretiva',
      'S': 'Preventiva',
      'A': 'Aferição',
      'D': 'Desinstalação',
      'R': 'Recarga',
      'B': 'Balcão'
    };
    return tipos[tipoOs] || '';
  };

  // Estados principais
  const [osType, setOsType] = useState(''); // 'contrato' ou 'estoque'
  // Tipo de OS (somente modo único: serial/patrimônio). Padrão: N = Corretiva.
  const [tipoOsUniqueValue, setTipoOsUniqueValue] = useState('N');
  const [tipoOsUniqueConfirmed, setTipoOsUniqueConfirmed] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null); // Contrato selecionado (quando searchMode === 'cliente')
  const [selectedEquipments, setSelectedEquipments] = useState([]); // Equipamentos selecionados do contrato
  const [confirmedContractEquipments, setConfirmedContractEquipments] = useState(null); // Equipamentos confirmados do contrato para exibir no EquipmentInfoStep
  const [defeitoEquipVinculoAtivo, setDefeitoEquipVinculoAtivo] = useState(false);

  // Estados de última OS
  const [lastOsDate, setLastOsDate] = useState(null);
  const [lastOsData, setLastOsData] = useState(null);
  const [isLoadingLastOs, setIsLoadingLastOs] = useState(false);
  const [showDefeitoDetails, setShowDefeitoDetails] = useState(false);
  const [showLaudo, setShowLaudo] = useState(false);
  const [showLastOsSection, setShowLastOsSection] = useState(false);
  
  // Estados de formulário
  const [solicitante, setSolicitante] = useState('');
  const [selectedDefeito, setSelectedDefeito] = useState(null);
  const [defeitoObservacao, setDefeitoObservacao] = useState('');
  const [contatoFone1, setContatoFone1] = useState('');
  const [contatoWhatsApp, setContatoWhatsApp] = useState('');
  const [contatoFone2, setContatoFone2] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Estados de notificação
  const [showOpenOsNotification, setShowOpenOsNotification] = useState(false);
  const [openOsNumbers, setOpenOsNumbers] = useState([]);
  const [pendingEquipmentOpenOS, setPendingEquipmentOpenOS] = useState(null);
  const [showRetiradaAtivaNotification, setShowRetiradaAtivaNotification] = useState(false);
  const [retiradaAtivaCodigos, setRetiradaAtivaCodigos] = useState([]);
  
  // Estados de contadores
  const [showCounters, setShowCounters] = useState(false);
  const [countersConfirmed, setCountersConfirmed] = useState(false);
  const [loadingCounters, setLoadingCounters] = useState(false);
  const [lastCounters, setLastCounters] = useState({ pb: 0, color: 0, total: 0 });
  const [counterValues, setCounterValues] = useState({ pb: '', color: '', total: '' });
  const [counterErrors, setCounterErrors] = useState({ pb: '', color: '', total: '' });
  
  // Estados de programação
  const [showScheduleDropdown, setShowScheduleDropdown] = useState(false);
  const [showScheduleSidebar, setShowScheduleSidebar] = useState(false);
  const [scheduleType, setScheduleType] = useState(null);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState(null);
  const [scheduleNoCertainDate, setScheduleNoCertainDate] = useState(false);
  const [futuraStandbyNotes, setFuturaStandbyNotes] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleAreasData, setScheduleAreasData] = useState([]);
  const [isLoadingScheduleData, setIsLoadingScheduleData] = useState(false);
  const [showScheduleTooltip, setShowScheduleTooltip] = useState(false);
  const [expandedAreas, setExpandedAreas] = useState(new Set());
  
  // Estados para criação de OS
  const [isCreatingOS, setIsCreatingOS] = useState(false);
  const [showOSResultModal, setShowOSResultModal] = useState(false);
  const [osResult, setOsResult] = useState(null); // { success: boolean, osNumber?: string, errors?: Array }
  // Estados para criação em lote (modo contrato)
  const [isCreatingMultipleOS, setIsCreatingMultipleOS] = useState(false);
  const [multipleOSProgress, setMultipleOSProgress] = useState({ current: 0, total: 0, currentSerie: '' });
  const [multipleOSResults, setMultipleOSResults] = useState([]); // Array de { serie, patrimonio, osNumber?, error? }
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [osNumberCopied, setOsNumberCopied] = useState(false);
  
  // Estados para envio de email
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const [loadingPdfPrint, setLoadingPdfPrint] = useState(null); // 'pdf' | 'print' | null — enquanto PDF/impressão não for entregue
  const [autoEmailResult, setAutoEmailResult] = useState(null); // { success, message, to }
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailToSend, setEmailToSend] = useState('');
  const [emailSendError, setEmailSendError] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  
  // Preencher email quando modal de email abrir
  useEffect(() => {
    if (showEmailModal) {
      setEmailToSend(email || '');
      setEmailSendError('');
      setEmailSentSuccess(false);
    }
  }, [showEmailModal, email]);
  
  // Fechar dropdown ao clicar fora (não fechar enquanto PDF/impressão estiver em andamento)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptionsDropdown && !loadingPdfPrint && !event.target.closest('[data-options-dropdown]')) {
        setShowOptionsDropdown(false);
      }
    };
    
    if (showOptionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showOptionsDropdown, loadingPdfPrint]);
  
  // Validar email
  const validateEmail = (emailValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };
  
  // Função para enviar email
  const handleSendEmail = async () => {
    if (!validateEmail(emailToSend)) {
      setEmailSendError('Email inválido');
      return;
    }
    
    setIsSendingEmail(true);
    setEmailSendError('');
    
    try {
      // Preparar dados para o template
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      const dataFormatada = `${day}/${month}/${year}`;
      
      // Dados da OS (modo único)
      const osNumber = osResult?.osNumber || '000000';
      const equipmentData = selectedEquipment || {};
      const empresaNome = equipmentData.emp_nome || 'NOME DA EMPRESA';
      const modeloEquipamento = equipmentData.produto || '';
      const numeroSerie = equipmentData.serie || '';
      const patrimonio = equipmentData.patrimonio || '';
      const enderecoInstalacao = equipmentData.Endereco 
        ? (equipmentData.Endereco + (equipmentData.Endereco_Local ? ` - ${equipmentData.Endereco_Local}` : ''))
        : '';
      const defeitoNome = selectedDefeito ? (selectedDefeito.nome || selectedDefeito.label || '') : '';
      
      // Template HTML
      const emailHTML = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Abertura de Ordem de Serviço</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
    
    <!-- Container principal -->
    <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5;">
        <tr>
            <td align="center" style="padding: 20px;">
                
                <!-- Email wrapper -->
                <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); overflow: hidden;">
                    
                    <!-- Header com número da OS -->
                    <tr>
                        <td style="background: linear-gradient(135deg, #e8f0f7 0%, #f0f4f8 100%); padding: 28px 16px; text-align: center; border-bottom: 2px solid #d0dce6;">
                            <h1 style="margin: 0; color: #1a3a52; font-size: 28px; font-weight: 600;">Ordem de serviço aberta</h1>
                            <p style="margin: 10px 0 0 0; color: #5a7a8a; font-size: 16px;">Número: <strong style="color: #2c5aa0; font-size: 20px;">${osNumber}</strong></p>
                        </td>
                    </tr>
                    
                    <!-- Conteúdo principal -->
                    <tr>
                        <td style="padding: 30px;">
                            
                            <!-- Mensagem de boas-vindas -->
                            <p style="margin: 0 0 25px 0; color: #333333; font-size: 15px; line-height: 1.6;">
                                Prezado cliente,<br><br>
                                Confirmamos a abertura de uma nova ordem de serviço para o seu equipamento. Abaixo você encontra todos os detalhes do chamado.
                            </p>
                            
                            <!-- SEÇÃO 1: Dados do Chamado -->
                            <div style="margin-bottom: 25px;">
                                <h2 style="margin: 0 0 15px 0; color: #2c5aa0; font-size: 16px; font-weight: 600; border-bottom: 2px solid #e8f0f7; padding-bottom: 10px;">Dados do Chamado</h2>
                                <table width="100%" cellpadding="0" cellspacing="0">
                                    <tr>
                                        <td style="padding: 10px 0; color: #666666; font-size: 14px; width: 50%;">
                                            <strong style="color: #333333;">Número da OS:</strong><br>
                                            <span style="color: #2c5aa0; font-size: 15px; font-weight: 500;">${osNumber}</span>
                                        </td>
                                        <td style="padding: 10px 0 10px 20px; color: #666666; font-size: 14px; width: 50%;">
                                            <strong style="color: #333333;">Data de Abertura:</strong><br>
                                            <span style="color: #555555;">${dataFormatada}</span>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                            
                            <!-- SEÇÃO 2: Dados do Equipamento -->
                            <div style="margin-bottom: 25px;">
                                <h2 style="margin: 0 0 15px 0; color: #2c5aa0; font-size: 16px; font-weight: 600; border-bottom: 2px solid #e8f0f7; padding-bottom: 10px;">Dados do Equipamento</h2>
                                <table width="100%" cellpadding="0" cellspacing="0">
                                    <tr>
                                        <td style="padding: 10px 0; color: #666666; font-size: 14px; width: 50%;">
                                            <strong style="color: #333333;">Modelo:</strong><br>
                                            <span style="color: #555555;">${modeloEquipamento}</span>
                                        </td>
                                        <td style="padding: 10px 0 10px 20px; color: #666666; font-size: 14px; width: 50%;">
                                            <strong style="color: #333333;">Número de Série:</strong><br>
                                            <span style="color: #555555;">${numeroSerie}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colspan="2" style="padding: 10px 0; color: #666666; font-size: 14px;">
                                            <strong style="color: #333333;">Número de Patrimônio:</strong><br>
                                            <span style="color: #555555;">${patrimonio || 'N/A'}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colspan="2" style="padding: 10px 0; color: #666666; font-size: 14px;">
                                            <strong style="color: #333333;">Endereço de Instalação:</strong><br>
                                            <span style="color: #555555;">${enderecoInstalacao || 'N/A'}</span>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                            
                            <!-- SEÇÃO 3: Dados de Contato -->
                            <div style="margin-bottom: 25px;">
                                <h2 style="margin: 0 0 15px 0; color: #2c5aa0; font-size: 16px; font-weight: 600; border-bottom: 2px solid #e8f0f7; padding-bottom: 10px;">Dados de Contato</h2>
                                <table width="100%" cellpadding="0" cellspacing="0">
                                    <tr>
                                        <td style="padding: 10px 0; color: #666666; font-size: 14px; width: 50%;">
                                            <strong style="color: #333333;">Solicitante:</strong><br>
                                            <span style="color: #555555;">${solicitante || 'N/A'}</span>
                                        </td>
                                        <td style="padding: 10px 0 10px 20px; color: #666666; font-size: 14px; width: 50%;">
                                            <strong style="color: #333333;">Contato:</strong><br>
                                            <span style="color: #555555;">${contatoFone1 || 'N/A'}</span>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                            
                            <!-- SEÇÃO 4: Descrição do Defeito -->
                            <div style="margin-bottom: 25px;">
                                <h2 style="margin: 0 0 15px 0; color: #2c5aa0; font-size: 16px; font-weight: 600; border-bottom: 2px solid #e8f0f7; padding-bottom: 10px;">Descrição do Defeito</h2>
                                <div style="padding: 15px; background-color: #f9fafb; border-left: 4px solid #2c5aa0; border-radius: 4px;">
                                    <p style="margin: 0; color: #555555; font-size: 14px; line-height: 1.6;">${defeitoNome || 'N/A'}</p>
                                </div>
                            </div>
                            
                            <!-- SEÇÃO 5: Observações (opcional) -->
                            <div style="margin-bottom: 25px;">
                                <h2 style="margin: 0 0 15px 0; color: #2c5aa0; font-size: 16px; font-weight: 600; border-bottom: 2px solid #e8f0f7; padding-bottom: 10px;">Observações Adicionais</h2>
                                <div style="padding: 15px; background-color: #f9fafb; border-left: 4px solid #d0dce6; border-radius: 4px;">
                                    <p style="margin: 0; color: #555555; font-size: 14px; line-height: 1.6;">${defeitoObservacao || 'Nenhuma observação adicional.'}</p>
                                </div>
                            </div>
                            
                            <!-- Mensagem de encerramento -->
                            <p style="margin: 30px 0 0 0; color: #666666; font-size: 14px; line-height: 1.6;">
                                Nossa equipe técnica entrará em contato em breve para agendar a assistência. Caso tenha dúvidas, entre em contato conosco.
                            </p>
                            
                        </td>
                    </tr>
                    
                    <!-- Footer -->
                    <tr>
                        <td style="background-color: #f9fafb; padding: 20px 30px; border-top: 1px solid var(--border-subtle, #e8e8e8);">
                            <p style="margin: 0; color: #888888; font-size: 12px; line-height: 1.6;">
                                ${empresaNome}<br>
                            </p>
                        </td>
                    </tr>
                    
                </table>
                
            </td>
        </tr>
    </table>
    
</body>
</html>`;
      
      // Obter userId do localStorage
      const userDataStr = localStorage.getItem('userData');
      const userData = userDataStr ? JSON.parse(userDataStr) : null;
      const userId = userData?.id || null;
      
      if (!userId) {
        throw new Error('Usuário não identificado. Por favor, faça login novamente.');
      }
      
      // Chamar endpoint para enviar email
      const API_BASE_URL = externalApiUrl || process.env.REACT_APP_API_URL || 'http://localhost:3002';
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        throw new Error('Sessão não autenticada. Por favor, faça login novamente.');
      }
      
      const response = await fetch(`${API_BASE_URL}/api/os/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          to: emailToSend,
          subject: `Ordem de Serviço ${osNumber} - Abertura confirmada`,
          html: emailHTML,
          osNumber
        })
      });
      
      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Erro ao enviar email');
      }
      
      // Sucesso - mostrar mensagem de sucesso animada
      setEmailSentSuccess(true);
      
      // Fechar modal após 2.5 segundos
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailSentSuccess(false);
        setEmailToSend('');
        setEmailSendError('');
      }, 2500);
    } catch (error) {
      console.error('Erro ao enviar email:', error);
      setEmailSendError(error.message || 'Erro ao enviar email. Tente novamente.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Handler para quando equipamento é selecionado
  const handleEquipmentSelect = useCallback(async (equipment) => {
    // Resetar estados de contadores e programação quando um novo equipamento é selecionado
    setShowCounters(false);
    setCountersConfirmed(false);
    setCounterValues({ pb: '', color: '', total: '' });
    setCounterErrors({ pb: '', color: '', total: '' });
    setShowScheduleDropdown(false);
    setShowScheduleSidebar(false);
    setScheduleType(null);
    setSelectedScheduleDate(null);
    setScheduleNoCertainDate(false);
    setSelectedTechnician(null);
    setIsScheduled(false);
    setScheduleData(null);
    setExpandedAreas(new Set());
    
    // Verificar se é um contrato (tem flag isContract ou tem codigo mas não tem serie)
    if (equipment && (equipment.isContract || (equipment.codigo && !equipment.serie))) {
      // É um contrato selecionado
      setSelectedContract(equipment);
      setSelectedEquipments([]);
      setSelectedEquipment(null);
      return;
    }
    
    // É um equipamento normal
    setSelectedContract(null);
    setSelectedEquipments([]);
    setSelectedEquipment(equipment);
    if (equipment) {
      setIsLoadingLastOs(true);
      try {
        const result = await getLastOS(equipment.serie);
        if (result.success) {
          setLastOsDate(result.lastOsDate);
          setLastOsData(result.lastOsData);
        }
      } catch (error) {
        console.error('Erro ao buscar última OS:', error);
      } finally {
        setIsLoadingLastOs(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!pendingEquipmentOpenOS) return;
    if (showOpenOsNotification || showRetiradaAtivaNotification) return;
    const eq = pendingEquipmentOpenOS;
    setPendingEquipmentOpenOS(null);
    handleEquipmentSelect(eq);
  }, [pendingEquipmentOpenOS, showOpenOsNotification, showRetiradaAtivaNotification, handleEquipmentSelect]);

  // Handler para atualizar equipamento (quando endereço ou local interno é editado)
  const handleEquipmentUpdate = useCallback((updatedEquipment) => {
    setSelectedEquipment(updatedEquipment);
  }, []);

  // Função para carregar dados de programação
  const handleLoadScheduleData = useCallback(async () => {
    setIsLoadingScheduleData(true);
    try {
      let equipmentModelFilter = null;
      if (confirmedContractEquipments?.equipments?.length) {
        const fe = confirmedContractEquipments.equipments[0];
        const b = fe.marca != null ? String(fe.marca).trim() : '';
        const s = fe.subgrupo != null ? String(fe.subgrupo).trim() : '';
        if (b && s) equipmentModelFilter = { idBrand: b, idSubgroup: s };
      } else if (selectedEquipment) {
        const b = selectedEquipment.idBrand != null ? String(selectedEquipment.idBrand).trim() : '';
        const s = selectedEquipment.idSubgroup != null ? String(selectedEquipment.idSubgroup).trim() : '';
        if (b && s) equipmentModelFilter = { idBrand: b, idSubgroup: s };
      }
      const result = await loadScheduleData(equipmentModelFilter);
      if (result.success) {
        setScheduleAreasData(result.data || []);
      } else {
        setScheduleAreasData([]);
      }
    } catch (error) {
      console.error('Erro ao carregar dados de programação:', error);
      setScheduleAreasData([]);
    } finally {
      setIsLoadingScheduleData(false);
    }
  }, [selectedEquipment, confirmedContractEquipments]);

  // Fechar dropdown de programação ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showScheduleDropdown && !event.target.closest('[data-schedule-dropdown]')) {
        setShowScheduleDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showScheduleDropdown]);

  // Função para limpar todos os estados ao fechar o modal
  const handleClose = useCallback(() => {
    setOsType('');
    setSelectedEquipment(null);
    setSelectedContract(null);
    setSelectedEquipments([]);
    setConfirmedContractEquipments(null);
    setLastOsDate(null);
    setLastOsData(null);
    setShowDefeitoDetails(false);
    setShowLaudo(false);
    setShowLastOsSection(false);
    setSolicitante('');
    setSelectedDefeito(null);
    setDefeitoObservacao('');
    setContatoFone1('');
    setContatoWhatsApp('');
    setContatoFone2('');
    setEmail('');
    setEmailError('');
    setShowOpenOsNotification(false);
    setOpenOsNumbers([]);
    setPendingEquipmentOpenOS(null);
    setShowRetiradaAtivaNotification(false);
    setRetiradaAtivaCodigos([]);
    setShowCounters(false);
    setCountersConfirmed(false);
    setCounterValues({ pb: '', color: '', total: '' });
    setCounterErrors({ pb: '', color: '', total: '' });
    setLastCounters({ pb: 0, color: 0, total: 0 });
    setShowScheduleDropdown(false);
    setShowScheduleSidebar(false);
    setScheduleType(null);
    setSelectedScheduleDate(null);
    setScheduleNoCertainDate(false);
    setSelectedTechnician(null);
    setIsScheduled(false);
    setScheduleData(null);
    setScheduleAreasData([]);
    setIsLoadingScheduleData(false);
    setExpandedAreas(new Set());
    setOsResult(null);
    setShowOSResultModal(false);
    setIsCreatingOS(false);
    setIsCreatingMultipleOS(false);
    setMultipleOSProgress({ current: 0, total: 0, currentSerie: '' });
    setMultipleOSResults([]);
    setShowErrorDetails(false);
    setOsNumberCopied(false);
    setAutoEmailResult(null);
    
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const tipoOsValidationExtras = useMemo(
    () => ({
      requerTipoOsPorDefeito:
        defeitoEquipVinculoAtivo &&
        osType !== 'estoque' &&
        !(confirmedContractEquipments?.equipments?.length > 0),
      tipoOsValue: tipoOsUniqueValue,
    }),
    [defeitoEquipVinculoAtivo, osType, confirmedContractEquipments, tipoOsUniqueValue]
  );

  // Validação de campos obrigatórios
  const validateRequiredFields = useCallback(() => {
    return validateFields(solicitante, contatoFone1, selectedDefeito, defeitoObservacao, tipoOsValidationExtras);
  }, [solicitante, contatoFone1, selectedDefeito, defeitoObservacao, tipoOsValidationExtras]);

  // Função auxiliar para preparar osData de um equipamento (modo contrato)
  const prepareEquipmentOSData = useCallback((equipment, nomeUsuario, userId, tipoOs) => {
    // Determinar CODTEC (4 dígitos quando from programação/apenas técnico)
    let codTec = null;
    if (isScheduled && scheduleData && scheduleData.technician) {
      const raw = scheduleData.technician.id_tech || scheduleData.technician.id;
      codTec = (raw != null && String(raw).trim() !== '') ? String(raw).padStart(4, '0') : null;
    }
    // No modo contrato, não temos codTec do equipamento, então será NULL e o backend buscará automaticamente

    // Determinar LOCALIZACAO
    const localizacao = equipment.endereco?.local || '';

    // Determinar campos de endereço (dados diretos do equipamento)
    const endLogradouro = equipment.endereco?.logradouro || '';
    const endNum = equipment.endereco?.numero || '0';
    const endComp = equipment.endereco?.complemento || '';
    const endBairro = equipment.endereco?.bairro || '';
    const endCidade = equipment.endereco?.cidade || '';
    const endEstado = equipment.endereco?.estado || '';
    const endCep = equipment.endereco?.cep || '';

    // Determinar FONE (do formulário - Fone1 obrigatório)
    const fone = removeMask(contatoFone1.trim() || '');
    const fone2 = removeMask(contatoFone2.trim() || '');
    const foneWhats = removeMask(contatoWhatsApp.trim() || '');

    // Mapear tipo de OS para TIPOOS (parâmetro procedure)
    const tipoOsMap = {
      'C': 'N', // Corretiva
      'S': 'S', // Preventiva
      'A': 'A', // Aferição
      'D': 'D', // Desinstalação
      'R': 'R',  // Recarga
      'B': 'B'   // Balcão
    };
    const tipoOsValue = tipoOsMap[tipoOs] || 'N';

    return {
      NOME_USUARIO: nomeUsuario,
      CONTRATO: equipment.codigo || '',
      CODCLI: equipment.codCli || '',
      CODPROD: equipment.codProduto || '',
      SERIE: equipment.numSerie || '',
      ATENDENTE: nomeUsuario,
      SOLICITANTE: solicitante.trim(),
      CODEMP: equipment.codEmp || '',
      MOTIVO_OBS_DEFEITO: defeitoObservacao.trim(),
      CODTEC: codTec,
      CONTADOR_PB: null,
      CONTADOR_COR: null,
      CONTADOR_TOTAL: null,
      END_LOGRADOURO: endLogradouro,
      END_NUM: endNum,
      END_LOGRADOURO_COMP: endComp,
      END_BAIRRO: endBairro,
      END_CIDADE: endCidade,
      END_ESTADO: endEstado,
      END_CEP: endCep,
      FONE: fone,
      EMAIL: email.trim() || '',
      CODSITE: equipment.codSite || '',
      CONTATO: contatoFone1.trim(),
      CONTATO_FONE1: contatoFone1.trim(),
      CONTATO_WHATSAPP: contatoWhatsApp.trim(),
      CONTATO_FONE2: contatoFone2.trim(),
      LOCALIZACAO: localizacao,
      CODIGO_DEFEITO: selectedDefeito?.codigo || '',
      ORIGEM: 'C',
      TIPOOS: tipoOsValue,
      FONE2: fone2,
      FONEWHATS: foneWhats,
      // Dados para inserção em LCMY_REG_OS_STANDBY (se necessário; não inserir quando "Não há data certa")
      scheduleType: scheduleType,
      scheduleDate: scheduleData?.noCertainDate ? null : (scheduleData?.date || null),
      scheduleTechnicianId: scheduleData?.technician?.id_tech || scheduleData?.technician?.id || null,
      standbyNotes: scheduleData?.noCertainDate
        ? String(scheduleData.standbyNotes || '').trim() || null
        : null,
      userId: userId
    };
  }, [
    solicitante,
    contatoFone1,
    contatoWhatsApp,
    contatoFone2,
    email,
    selectedDefeito,
    defeitoObservacao,
    isScheduled,
    scheduleData,
    scheduleType
  ]);

  // Handler para criar múltiplas OS (modo contrato)
  const handleCreateMultipleOS = useCallback(async () => {
    if (!confirmedContractEquipments || !confirmedContractEquipments.equipments || confirmedContractEquipments.equipments.length === 0) {
      return;
    }

    const equipments = confirmedContractEquipments.equipments;
    const tipoOs = confirmedContractEquipments.tipoOs; // 'C', 'S', 'A', ou 'D'

    if (!tipoOs) {
      alert('Tipo de OS não selecionado. Por favor, selecione o tipo na etapa anterior.');
      return;
    }

    if (isScheduled && scheduleData?.type === 'future' && scheduleData?.noCertainDate) {
      const sn = String(scheduleData.standbyNotes || '').trim();
      if (sn.length < 10) {
        alert('Para agendamento futuro sem data definida, informe observações com no mínimo 10 caracteres na programação.');
        return;
      }
    }

    setIsCreatingMultipleOS(true);
    setMultipleOSProgress({ current: 0, total: equipments.length, currentSerie: '' });
    setMultipleOSResults([]);

    // Obter dados do usuário logado
    const userDataStr = localStorage.getItem('userData');
    const userData = userDataStr ? JSON.parse(userDataStr) : null;
    const userName = userData?.username || userData?.name || '';
    const userEmail = userData?.email || '';
    const userId = userData?.id || null;
    
    // Extrair nome do usuário antes do @ do email
    const nomeUsuario = userEmail && userEmail.includes('@') 
      ? userEmail.split('@')[0] 
      : userName;

    // Buscar código de status baseado no tipo de OS
    const statusResult = await getStatusCode(scheduleType, 'contrato', tipoOs);
    if (!statusResult.success || !statusResult.codStatus) {
      alert('Não foi possível obter o código de status');
      setIsCreatingMultipleOS(false);
      return;
    }

    const results = [];

    // Iterar sobre cada equipamento
    for (let i = 0; i < equipments.length; i++) {
      const equipment = equipments[i];
      
      setMultipleOSProgress({
        current: i + 1,
        total: equipments.length,
        currentSerie: equipment.numSerie || equipment.patrimonio || ''
      });

      try {
        // Preparar dados da OS para este equipamento
        const osData = prepareEquipmentOSData(equipment, nomeUsuario, userId, tipoOs);
        osData.CODSTATUS = statusResult.codStatus;

        // Executar criação da OS
        const result = await createOS(osData);

        if (result.success && result.osNumber) {
          results.push({
            serie: equipment.numSerie || '',
            patrimonio: equipment.patrimonio || '',
            osNumber: result.osNumber,
            success: true,
            error: null
          });
        } else {
          // Capturar erro da procedure
          const errorMessage = result.errors && result.errors.length > 0
            ? result.errors[0].ErrorMessage || 'Erro desconhecido'
            : 'Erro ao criar ordem de serviço';
          
          results.push({
            serie: equipment.numSerie || '',
            patrimonio: equipment.patrimonio || '',
            osNumber: null,
            success: false,
            error: errorMessage
          });
        }
      } catch (error) {
        console.error(`Erro ao criar OS para série ${equipment.numSerie}:`, error);
        results.push({
          serie: equipment.numSerie || '',
          patrimonio: equipment.patrimonio || '',
          osNumber: null,
          success: false,
          error: error.message || 'Erro desconhecido ao criar ordem de serviço'
        });
      }
    }

    // Atualização da coluna no Board: "Apenas técnico" → Em aberto (refreshOpenOrders)
    const isApenasTecnico = scheduleType === 'apenas_tecnico';
    const wasScheduled = (scheduleType && scheduleData?.technician) && !isApenasTecnico;
    const technicianId = wasScheduled
      ? (scheduleData.technician.id_tech || scheduleData.technician.id)
      : null;

    if (results.length > 0) {
      results[0]._scheduled = wasScheduled;
      results[0]._technicianId = technicianId;
    }
    setMultipleOSResults(results);
    setIsCreatingMultipleOS(false);
    setShowOSResultModal(true);
  }, [
    confirmedContractEquipments,
    solicitante,
    contatoFone1,
    contatoWhatsApp,
    contatoFone2,
    email,
    selectedDefeito,
    defeitoObservacao,
    scheduleType,
    isScheduled,
    scheduleData,
    prepareEquipmentOSData
  ]);

  // Handler para criar OS
  const handleCreateOS = useCallback(async (tipoOsOverride) => {
    const validation = validateRequiredFieldsWithErrors(
      solicitante,
      contatoFone1,
      selectedDefeito,
      defeitoObservacao,
      tipoOsValidationExtras
    );
    if (!validation.isValid) {
      alert('Por favor, corrija os seguintes campos:\n\n' + validation.errors.join('\n'));
      return;
    }

    // Verificar se está em modo contrato
    const isContractMode = confirmedContractEquipments && confirmedContractEquipments.equipments && confirmedContractEquipments.equipments.length > 0;
    
    if (!isContractMode && !selectedEquipment) {
      alert('Por favor, selecione um equipamento');
      return;
    }

    if (isScheduled && scheduleData?.type === 'future' && scheduleData?.noCertainDate) {
      const sn = String(scheduleData.standbyNotes || '').trim();
      if (sn.length < 10) {
        alert('Para agendamento futuro sem data definida, informe observações com no mínimo 10 caracteres na programação.');
        return;
      }
    }

    if (isContractMode) {
      // Modo contrato: criar OS para múltiplos equipamentos
      await handleCreateMultipleOS();
      return;
    }

    // Modo único: criar OS para um único equipamento
    setIsCreatingOS(true);

    try {
      // Obter dados do usuário logado
      const userDataStr = localStorage.getItem('userData');
      const userData = userDataStr ? JSON.parse(userDataStr) : null;
      const userName = userData?.username || userData?.name || '';
      const userEmail = userData?.email || '';
      const userId = userData?.id || null;
      
      // Extrair nome do usuário antes do @ do email
      const nomeUsuario = userEmail && userEmail.includes('@') 
        ? userEmail.split('@')[0] 
        : userName;

      // Buscar código de status baseado no tipo de programação e tipo de OS
      const effectiveTipoOs =
        osType === 'estoque'
          ? null
          : typeof tipoOsOverride === 'string' && tipoOsOverride !== ''
            ? tipoOsOverride
            : 'N';
      const statusResult = await getStatusCode(scheduleType, osType, effectiveTipoOs);
      if (!statusResult.success || !statusResult.codStatus) {
        throw new Error('Não foi possível obter o código de status');
      }

      // Determinar CODTEC (4 dígitos quando from programação/apenas técnico)
      let codTec = null;
      if (isScheduled && scheduleData && scheduleData.technician) {
        const raw = scheduleData.technician.id_tech || scheduleData.technician.id;
        codTec = (raw != null && String(raw).trim() !== '') ? String(raw).padStart(4, '0') : null;
      } else if (selectedEquipment.codTec) {
        codTec = selectedEquipment.codTec;
      }
      
      // Se ainda for nulo, o backend buscará automaticamente o primeiro técnico ativo

      // Determinar LOCALIZACAO (prioritário: local interno editado, senão do banco)
      const localizacao = selectedEquipment.Endereco_Local && selectedEquipment.Endereco_Local.trim()
        ? selectedEquipment.Endereco_Local.trim()
        : (selectedEquipment.Endereco_Local || '');

      // Determinar campos de endereço (prioritário: endereço editado, senão do equipamento)
      // Verificar explicitamente se os campos corrigidos existem e não são vazios
      const hasCorrectedAddress = selectedEquipment.corrected_end_logradouro && 
                                   selectedEquipment.corrected_end_logradouro.trim() !== '';
      
      const endLogradouro = hasCorrectedAddress 
        ? (selectedEquipment.corrected_end_logradouro || '')
        : (selectedEquipment.end_logradouro || '');
      
      const endNum = hasCorrectedAddress
        ? (selectedEquipment.corrected_end_num || '0')
        : (selectedEquipment.end_num || '0');
      
      const endComp = hasCorrectedAddress
        ? (selectedEquipment.corrected_end_comp || '')
        : (selectedEquipment.end_comp || '');
      
      const endBairro = hasCorrectedAddress
        ? (selectedEquipment.corrected_end_bairro || '')
        : (selectedEquipment.end_bairro || '');
      
      const endCidade = hasCorrectedAddress
        ? (selectedEquipment.corrected_end_cidade || '')
        : (selectedEquipment.end_cidade || '');
      
      const endEstado = hasCorrectedAddress
        ? (selectedEquipment.corrected_end_estado || '')
        : (selectedEquipment.end_estado || '');
      
      const endCep = hasCorrectedAddress
        ? (selectedEquipment.corrected_end_cep || '')
        : (selectedEquipment.end_cep || '');
      
      // Determinar FONE (prioritário: CONTATO do formulário, senão end_fone do equipamento)
      // Remover máscara do telefone antes de enviar para a procedure
      const foneRaw = contatoFone1.trim() || selectedEquipment.end_fone || '';
      const fone = removeMask(foneRaw);
      const fone2 = removeMask(contatoFone2.trim() || '');
      const foneWhats = removeMask(contatoWhatsApp.trim() || '');

      // Preparar dados da OS
      const osData = {
        NOME_USUARIO: nomeUsuario,
        CONTRATO: selectedEquipment.codigo || '',
        CODCLI: selectedEquipment.codCli || '',
        CODPROD: selectedEquipment.codprod || '',
        SERIE: selectedEquipment.serie || '',
        ATENDENTE: nomeUsuario,
        SOLICITANTE: solicitante.trim(),
        CODEMP: selectedEquipment.codEmp || '',
        MOTIVO_OBS_DEFEITO: defeitoObservacao.trim(),
        CODTEC: codTec,
        CONTADOR_PB: countersConfirmed && counterValues.pb ? parseInt(counterValues.pb) || 0 : 0,
        CONTADOR_COR: countersConfirmed && counterValues.color ? parseInt(counterValues.color) || 0 : 0,
        CONTADOR_TOTAL: countersConfirmed && counterValues.total ? parseInt(counterValues.total) || 0 : 0,
        END_LOGRADOURO: endLogradouro,
        END_NUM: endNum,
        END_LOGRADOURO_COMP: endComp,
        END_BAIRRO: endBairro,
        END_CIDADE: endCidade,
        END_ESTADO: endEstado,
        END_CEP: endCep,
        FONE: fone,
        EMAIL: (email?.trim() || '') ? email.trim() : null,
        CODSITE: selectedEquipment.codSite || '',
        CONTATO: contatoFone1.trim(),
        CONTATO_FONE1: contatoFone1.trim(),
        CONTATO_WHATSAPP: contatoWhatsApp.trim(),
        CONTATO_FONE2: contatoFone2.trim(),
        LOCALIZACAO: localizacao,
        CODIGO_DEFEITO: selectedDefeito?.codigo || '',
        CODSTATUS: statusResult.codStatus,
        ORIGEM: osType === 'estoque' ? 'E' : 'C', // 'C' para CONTRATO, 'E' para ESTOQUE
        // N = Corretiva (modo único). Quando aplicável, desfixar pelo dropdown.
        TIPOOS:
          osType === 'estoque'
            ? 'E'
            : typeof tipoOsOverride === 'string' && tipoOsOverride !== ''
              ? tipoOsOverride
              : 'N',
        FONE2: fone2,
        FONEWHATS: foneWhats,
        // Dados para inserção em LCMY_REG_OS_STANDBY (se necessário; não inserir quando "Não há data certa")
        scheduleType: scheduleType,
        scheduleDate: scheduleData?.noCertainDate ? null : (scheduleData?.date || null),
        scheduleTechnicianId: scheduleData?.technician?.id_tech || scheduleData?.technician?.id || null,
        standbyNotes: scheduleData?.noCertainDate
          ? String(scheduleData.standbyNotes || '').trim()
          : null,
        userId: userId
      };

      // Executar criação da OS
      const result = await createOS(osData);

      // Definir resultado para atualização da coluna no Board: "Apenas técnico" → Em aberto (refreshOpenOrders)
      const isApenasTecnico = scheduleType === 'apenas_tecnico';
      const wasScheduled = (scheduleType && scheduleData?.technician) && !isApenasTecnico;
      const technicianId = wasScheduled
        ? (scheduleData.technician.id_tech || scheduleData.technician.id)
        : null;

      setOsResult({
        ...result,
        scheduled: wasScheduled,
        technicianId: technicianId
      });
      setOsNumberCopied(false);
      setAutoEmailResult(null);
      setShowOSResultModal(true);
      setIsCreatingOS(false);

      // Auto-enviar email se "Abertura da OS" estiver habilitado e campo email do formulário preenchido
      const recipientEmail = (email?.trim() || '').trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (result.success && result.osNumber && recipientEmail && emailRegex.test(recipientEmail)) {
        (async () => {
          try {
            const token = localStorage.getItem('authToken');
            if (!token) return;
            const nt = await fetchNotificationTypes(token);
            if (!nt.abertura_os) return;
            const today = new Date();
            const dataFormatada = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
            const equipmentData = selectedEquipment || {};
            const empresaNome = equipmentData.emp_nome || 'NOME DA EMPRESA';
            const defeitoObservacaoText = defeitoObservacao || 'Nenhuma observação adicional.';
            const emailHTML = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"></head><body style="margin:0;padding:0;font-family:sans-serif;background:#f5f5f5"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f5f5"><tr><td align="center" style="padding:20px"><table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;box-shadow:0 2px 4px rgba(0,0,0,0.1)"><tr><td style="background:linear-gradient(135deg,#e8f0f7,#f0f4f8);padding:28px 16px;text-align:center;border-bottom:2px solid #d0dce6"><h1 style="margin:0;color:#1a3a52;font-size:28px">Ordem de serviço aberta</h1><p style="margin:10px 0 0 0;color:#5a7a8a;font-size:16px">Número: <strong style="color:#2c5aa0;font-size:20px">${result.osNumber}</strong></p></td></tr><tr><td style="padding:30px"><p style="margin:0 0 25px 0;color:#333;font-size:15px;line-height:1.6">Prezado cliente,<br><br>Confirmamos a abertura de uma nova ordem de serviço para o seu equipamento.</p><div style="margin-bottom:25px"><h2 style="margin:0 0 15px 0;color:#2c5aa0;font-size:16px;font-weight:600;border-bottom:2px solid #e8f0f7;padding-bottom:10px">Dados do Chamado</h2><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:10px 0;color:#666;font-size:14px;width:50%"><strong style="color:#333">Número da OS:</strong><br><span style="color:#2c5aa0;font-size:15px;font-weight:500">${result.osNumber}</span></td><td style="padding:10px 0 10px 20px;color:#666;font-size:14px;width:50%"><strong style="color:#333">Data de Abertura:</strong><br><span style="color:#555">${dataFormatada}</span></td></tr></table></div><div style="margin-bottom:25px"><h2 style="margin:0 0 15px 0;color:#2c5aa0;font-size:16px;font-weight:600;border-bottom:2px solid #e8f0f7;padding-bottom:10px">Dados do Equipamento</h2><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:10px 0;color:#666;font-size:14px;width:50%"><strong style="color:#333">Modelo:</strong><br><span style="color:#555">${equipmentData.produto || ''}</span></td><td style="padding:10px 0 10px 20px;color:#666;font-size:14px;width:50%"><strong style="color:#333">Número de Série:</strong><br><span style="color:#555">${equipmentData.serie || ''}</span></td></tr><tr><td colspan="2" style="padding:10px 0;color:#666;font-size:14px"><strong style="color:#333">Endereço:</strong><br><span style="color:#555">${(equipmentData.Endereco || '') + (equipmentData.Endereco_Local ? ` - ${equipmentData.Endereco_Local}` : '') || 'N/A'}</span></td></tr></table></div><div style="margin-bottom:25px"><h2 style="margin:0 0 15px 0;color:#2c5aa0;font-size:16px;font-weight:600;border-bottom:2px solid #e8f0f7;padding-bottom:10px">Dados de Contato</h2><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:10px 0;color:#666;font-size:14px;width:50%"><strong style="color:#333">Solicitante:</strong><br><span style="color:#555">${solicitante || 'N/A'}</span></td><td style="padding:10px 0 10px 20px;color:#666;font-size:14px;width:50%"><strong style="color:#333">Contato:</strong><br><span style="color:#555">${contatoFone1 || 'N/A'}</span></td></tr></table></div><div style="margin-bottom:25px"><h2 style="margin:0 0 15px 0;color:#2c5aa0;font-size:16px;font-weight:600;border-bottom:2px solid #e8f0f7;padding-bottom:10px">Descrição do Defeito</h2><div style="padding:15px;background:#f9fafb;border-left:4px solid #2c5aa0;border-radius:4px"><p style="margin:0;color:#555;font-size:14px;line-height:1.6">${selectedDefeito ? (selectedDefeito.nome || selectedDefeito.label || '') : 'N/A'}</p></div></div><div style="margin-bottom:25px"><h2 style="margin:0 0 15px 0;color:#2c5aa0;font-size:16px;font-weight:600;border-bottom:2px solid #e8f0f7;padding-bottom:10px">Observações Adicionais</h2><div style="padding:15px;background:#f9fafb;border-left:4px solid #d0dce6;border-radius:4px"><p style="margin:0;color:#555;font-size:14px;line-height:1.6">${defeitoObservacaoText}</p></div></div><p style="margin:30px 0 0 0;color:#666;font-size:14px;line-height:1.6">Nossa equipe técnica entrará em contato em breve para agendar a assistência. Caso tenha dúvidas, entre em contato conosco.</p></td></tr><tr><td style="background:#f9fafb;padding:20px 30px;border-top:1px solid var(--border-subtle, #e8e8e8)"><p style="margin:0;color:#888;font-size:12px;line-height:1.6">${empresaNome}<br></p></td></tr></table></td></tr></table></body></html>`;
            const r = await sendOSEmailSystem(
              recipientEmail,
              `Ordem de Serviço ${result.osNumber} - Abertura confirmada`,
              emailHTML,
              selectedEquipment?.codEmp || equipmentData.codEmp || ''
            );
            setAutoEmailResult({ success: r.success, message: r.message || (r.success ? 'Enviado' : 'Erro'), to: recipientEmail });
          } catch (e) {
            setAutoEmailResult({ success: false, message: e.message || 'Erro ao enviar', to: recipientEmail });
          }
        })();
      }

    } catch (error) {
      console.error('Erro ao criar OS:', error);
      setOsResult({
        success: false,
        errors: [{
          ErrorMessage: error.message || 'Erro desconhecido ao criar ordem de serviço'
        }]
      });
      setShowOSResultModal(true);
      setIsCreatingOS(false);
    }
  }, [
    solicitante, 
    contatoFone1,
    contatoWhatsApp,
    contatoFone2, 
    selectedDefeito, 
    defeitoObservacao,
    selectedEquipment,
    scheduleType,
    isScheduled,
    scheduleData,
    countersConfirmed,
    counterValues,
    email,
    confirmedContractEquipments,
    osType,
    tipoOsValidationExtras
  ]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const base = getApiBaseUrl();
        const r = await fetch(`${base}/api/config/defeitos-verve/equip-os-assoc-flag`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const j = await r.json().catch(() => ({}));
        setDefeitoEquipVinculoAtivo(!!j.enabled);
      } catch {
        setDefeitoEquipVinculoAtivo(false);
      }
    })();
  }, [isOpen]);

  // Resetar estados quando o modal é aberto
  useEffect(() => {
    if (isOpen) {
      // Resetar estados de contadores e programação quando o modal é aberto
      setShowCounters(false);
      setCountersConfirmed(false);
      setTipoOsUniqueValue('N');
      setTipoOsUniqueConfirmed(false);
      setCounterValues({ pb: '', color: '', total: '' });
      setCounterErrors({ pb: '', color: '', total: '' });
      setShowScheduleDropdown(false);
      setShowScheduleSidebar(false);
      setScheduleType(null);
      setSelectedScheduleDate(null);
      setScheduleNoCertainDate(false);
      setFuturaStandbyNotes('');
      setSelectedTechnician(null);
      setIsScheduled(false);
      setScheduleData(null);
      setExpandedAreas(new Set());
    }
  }, [isOpen]);

  // Preparar dados para o modo contrato
  const contractModeData = useMemo(() => {
    if (!confirmedContractEquipments || !confirmedContractEquipments.equipments || confirmedContractEquipments.equipments.length === 0) {
      return null;
    }

    const equipments = confirmedContractEquipments.equipments;
    const contract = confirmedContractEquipments.contract;
    
    // Detectar se há múltiplos sites únicos
    const uniqueSites = new Set(equipments.map(eq => eq.codSite || eq.siteNome || '')).size;
    const hasMultipleSites = uniqueSites > 1;
    
    // Pegar o primeiro equipamento para dados básicos
    const firstEquipment = equipments[0];
    
    // Preparar objeto selectedEquipment compatível
    const equipmentData = {
      produto: firstEquipment.modelo || '',
      serie: firstEquipment.numSerie || '',
      patrimonio: firstEquipment.patrimonio || '',
      codigo: contract.codigo || '',
      codCli: firstEquipment.codCli || '',
      codprod: firstEquipment.codProduto || '',
      codEmp: firstEquipment.codEmp || '',
      codSite: firstEquipment.codSite || '',
      Endereco: hasMultipleSites ? '' : (firstEquipment.enderecoCompleto || ''),
      Endereco_Local: firstEquipment.endereco?.local || '',
      cliente: contract.classif || '',
      // Flags para modo contrato
      isContractMode: true,
      contractClassif: contract.classif || '',
      contractEndereco: hasMultipleSites ? '' : (firstEquipment.enderecoCompleto || ''),
      hasMultipleSites: hasMultipleSites,
      selectedEquipments: equipments,
      idBrand: firstEquipment.marca != null ? String(firstEquipment.marca).trim() : '',
      idSubgroup: firstEquipment.subgrupo != null ? String(firstEquipment.subgrupo).trim() : ''
    };
    
    return {
      equipmentData,
      contract,
      hasMultipleSites,
      equipments,
      tipoOs: confirmedContractEquipments.tipoOs || null
    };
  }, [confirmedContractEquipments]);

  // Se o modal não estiver aberto, não renderizar nada
  if (!isOpen) {
    return null;
  }

  // Por enquanto, retornar um placeholder que será substituído pelo código completo
  // Isso permite migração incremental
  return (
    <div
      className="open-os-modal-overlay"
      style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'var(--overlay, rgba(0, 0, 0, 0.5))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      backdropFilter: 'blur(4px)'
    }}>
      <div
        className="open-os-modal-dialog"
        style={{
        background: 'var(--modal-bg, #ffffff)',
        color: 'var(--text-body, #2d3748)',
        borderRadius: '16px',
        width: '80%',
        maxWidth: '1600px',
        height: '85vh',
        maxHeight: '900px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(124, 77, 255, 0.25)',
        overflow: 'hidden'
      }}>
        {/* Header do Modal */}
        <div style={{
          padding: '15px 28px',
          borderBottom: '1px solid var(--border-subtle, #e0e0e0)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'var(--bg-surface-alt, #f8f9fa)',
          flexShrink: 0
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 600,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-body, #2d3748)'
          }}>
            Abrir ordem de serviço
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: '1px solid var(--border-subtle, #cbd5e0)',
              borderRadius: '6px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '18px',
              color: 'var(--text-muted, #4a5568)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'var(--border-subtle, #e2e8f0)';
              e.target.style.borderColor = '#a0aec0';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.borderColor = 'var(--border-subtle, #cbd5e0)';
            }}
          >
            ✕
          </button>
        </div>

        {/* Notificação de OS Aberta */}
        {showOpenOsNotification && (
          <div
            className="open-os-guard-alert"
            style={{
              padding: '16px 28px',
              background: '#FFFBA3',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: 'slideDown 0.3s ease-out',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)'
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
            }}>
              <i className="bi bi-exclamation-triangle-fill" style={{ 
                fontSize: '20px', 
                color: '#ffffff' 
              }}></i>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: '#92400e',
                marginBottom: '4px'
              }}>
                Ordem(ns) de serviço em aberto
              </div>
              <div style={{
                fontSize: '13px',
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: '#78350f',
                lineHeight: '1.5'
              }}>
                Já existe(m) ordem(ns) de serviço em aberto para este equipamento:{' '}
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
                  {openOsNumbers.map((num, idx) => (
                    <React.Fragment key={String(num)}>
                      {idx > 0 ? (
                        <span style={{ color: '#78350f', userSelect: 'none' }} aria-hidden>
                          ,
                        </span>
                      ) : null}
                      {typeof onViewBoardOrderDetails === 'function' ? (
                        <button
                          type="button"
                          onClick={() => onViewBoardOrderDetails(num)}
                          title="Ver detalhes desta OS"
                          className="open-os-guard-alert-code"
                          style={{
                            color: '#92400e',
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            fontSize: '13px',
                            background: 'rgba(255, 255, 255, 0.5)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            textUnderlineOffset: '2px'
                          }}
                        >
                          {num}
                        </button>
                      ) : (
                        <strong
                          style={{
                            color: '#92400e',
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            fontSize: '13px',
                            background: 'rgba(255, 255, 255, 0.5)',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}
                        >
                          {num}
                        </strong>
                      )}
                    </React.Fragment>
                  ))}
                </span>
                <br />
                <span style={{ fontSize: '12px', opacity: 0.9 }}>
                  Não é possível prosseguir com a abertura de nova OS.
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <button
                className="open-os-guard-alert-continue"
                onClick={() => {
                  setShowOpenOsNotification(false);
                  setOpenOsNumbers([]);
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(245, 158, 11, 0.5)',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  color: '#92400e',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 2px rgba(245, 158, 11, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--modal-bg, #ffffff)';
                  e.currentTarget.style.borderColor = '#f59e0b';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(245, 158, 11, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.5)';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(245, 158, 11, 0.2)';
                }}
              >
                <i className="bi bi-arrow-right-circle" style={{ fontSize: '14px' }}></i>
                Desejo continuar
              </button>
              <button
                className="open-os-guard-alert-dismiss"
                onClick={() => {
                  setShowOpenOsNotification(false);
                  setOpenOsNumbers([]);
                  if (!showRetiradaAtivaNotification) {
                    setPendingEquipmentOpenOS(null);
                  }
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid rgb(245, 158, 11)',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#92400e',
                  transition: 'all 0.2s ease',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.borderColor = '#f59e0b';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.3)';
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {showRetiradaAtivaNotification && (
          <div
            className="open-os-guard-alert"
            style={{
              padding: '16px 28px',
              background: '#FFFBA3',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: 'slideDown 0.3s ease-out',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)'
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
            }}>
              <i className="bi bi-exclamation-triangle-fill" style={{
                fontSize: '20px',
                color: '#ffffff'
              }}></i>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: '#92400e',
                marginBottom: '4px'
              }}>
                Este equipamento possui retirada ativa em andamento
              </div>
              <div style={{
                fontSize: '13px',
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: '#78350f',
                lineHeight: '1.5'
              }}>
                Código da retirada:{' '}
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
                  {retiradaAtivaCodigos.map((cod, idx) => (
                    <React.Fragment key={String(cod)}>
                      {idx > 0 ? (
                        <span style={{ color: '#78350f', userSelect: 'none' }} aria-hidden>
                          ,
                        </span>
                      ) : null}
                      <strong
                        className="open-os-guard-alert-code"
                        style={{
                          color: '#92400e',
                          fontWeight: 600,
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          background: 'rgba(255, 255, 255, 0.5)',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}
                      >
                        {cod}
                      </strong>
                    </React.Fragment>
                  ))}
                </span>
                <br />
                <span style={{ fontSize: '12px', opacity: 0.9 }}>
                  Não é possível prosseguir com a abertura de nova OS sem confirmar.
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <button
                type="button"
                className="open-os-guard-alert-continue"
                onClick={() => {
                  setShowRetiradaAtivaNotification(false);
                  setRetiradaAtivaCodigos([]);
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(245, 158, 11, 0.5)',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  color: '#92400e',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 2px rgba(245, 158, 11, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--modal-bg, #ffffff)';
                  e.currentTarget.style.borderColor = '#f59e0b';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(245, 158, 11, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.5)';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(245, 158, 11, 0.2)';
                }}
              >
                <i className="bi bi-arrow-right-circle" style={{ fontSize: '14px' }}></i>
                Desejo continuar
              </button>
              <button
                type="button"
                className="open-os-guard-alert-dismiss"
                onClick={() => {
                  setShowRetiradaAtivaNotification(false);
                  setRetiradaAtivaCodigos([]);
                  if (!showOpenOsNotification) {
                    setPendingEquipmentOpenOS(null);
                  }
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid rgb(245, 158, 11)',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#92400e',
                  transition: 'all 0.2s ease',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.borderColor = '#f59e0b';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.3)';
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Corpo do Modal */}
        <div style={{
          flex: 1,
          padding: '28px',
          overflowY: 'auto',
          background: 'var(--bg-body, #fafafa)'
        }}>
          {/* Layout quando Laudo está aberto: LAUDO à esquerda, ETAPA 1 e CONFIRMAÇÃO em coluna à direita */}
          {showLaudo && selectedEquipment ? (
            <div style={{
              display: 'flex',
              gap: '20px',
              alignItems: 'flex-start'
            }}>
              {/* LAUDO à esquerda */}
              {lastOsData && (
                <LaudoStep
                  lastOsData={lastOsData}
                  selectedEquipment={selectedEquipment}
                  onClose={() => setShowLaudo(false)}
                />
              )}

              {/* Coluna à direita com ETAPA 1 e CONFIRMAÇÃO minimizadas */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                flexShrink: 0
              }}>
                {/* Versão Compacta da ETAPA 1 */}
                <div style={{
                  background: 'var(--modal-bg, #ffffff)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  border: '1px solid var(--border-subtle, #e8e8e8)',
                  boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  position: 'relative'
                }}>
                  {/* Badge circular com ícone de equipamento no canto superior direito */}
                  <div className="open-os-contract-check" style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: '#7c4dff',
                    border: '2px solid var(--modal-bg, #ffffff)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: '#ffffff',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                    zIndex: 10
                  }}>
                    <i className="bi bi-printer" style={{ fontSize: '12px' }}></i>
                  </div>
                  <div style={{
                    flex: 1,
                    minWidth: 0
                  }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-muted, #718096)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '2px'
                    }}>
                      {osType === 'contrato' ? 'Contrato' : 'Estoque'}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {selectedEquipment.serie}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedEquipment(null);
                      setLastOsDate(null);
                      setLastOsData(null);
                      setShowDefeitoDetails(false);
                      setShowLaudo(false);
                      setShowLastOsSection(false);
                      // Resetar estados de contadores e programação
                      setShowCounters(false);
                      setCountersConfirmed(false);
                      setCounterValues({ pb: '', color: '', total: '' });
                      setCounterErrors({ pb: '', color: '', total: '' });
                      setShowScheduleDropdown(false);
                      setShowScheduleSidebar(false);
                      setScheduleType(null);
                      setSelectedScheduleDate(null);
                      setScheduleNoCertainDate(false);
                      setSelectedTechnician(null);
                      setIsScheduled(false);
                      setScheduleData(null);
                      setExpandedAreas(new Set());
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 500,
                      color: 'var(--text-muted, #4a5568)',
                      background: 'var(--bg-surface-alt, #f7fafc)',
                      border: '1px solid var(--border-subtle, #e2e8f0)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      flexShrink: 0
                    }}
                    className="open-os-btn-secondary"
                    onMouseEnter={(e) => {
                      e.target.style.background = 'var(--border-subtle, #edf2f7)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                    }}
                  >
                    <i className="bi bi-pencil" style={{ fontSize: '11px' }}></i>
                    <span>Editar</span>
                  </button>
              </div>

                {/* Elemento minimizado dos contadores confirmados */}
                {countersConfirmed && showCounters && (
                  <div style={{
                    background: 'var(--modal-bg, #ffffff)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    border: '1px solid var(--border-subtle, #e8e8e8)',
                    boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    position: 'relative'
                  }}>
                    {/* Badge circular com ícone de medidor no canto superior direito */}
                    <div className="open-os-contract-check" style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#7c4dff',
                      border: '2px solid var(--modal-bg, #ffffff)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: '#ffffff',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                      zIndex: 10
                    }}>
                      <i className="bi bi-speedometer2" style={{ fontSize: '12px' }}></i>
            </div>

                    {/* Lista de contadores */}
            <div style={{
              display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      flex: 1,
                      minWidth: 0
                    }}>
                      {/* Contador P&B */}
                      <div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 500,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-muted, #718096)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '2px'
                        }}>
                          Contador P&B
                        </div>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-body, #2d3748)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {formatNumberWithMask(counterValues?.pb || '0')}
                        </div>
                      </div>

                      {/* Contador Cor */}
                      <div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 500,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-muted, #718096)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '2px'
                        }}>
                          Contador Cor
                        </div>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-body, #2d3748)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {formatNumberWithMask(counterValues?.color || '0')}
                        </div>
                      </div>

                      {/* Contador Total */}
                      <div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 500,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-muted, #718096)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '2px'
                        }}>
                          Contador Total
                        </div>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-body, #2d3748)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {formatNumberWithMask(counterValues?.total || '0')}
                        </div>
                      </div>
                    </div>

                    {/* Botão Editar */}
                    <button className="open-os-btn-secondary"
                      type="button"
                      onClick={() => {
                        // Permite editar novamente os contadores
                        setCountersConfirmed(false);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        fontWeight: 500,
                        color: 'var(--text-muted, #4a5568)',
                        background: 'var(--bg-surface-alt, #f7fafc)',
                        border: '1px solid var(--border-subtle, #e2e8f0)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'var(--border-subtle, #edf2f7)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                      }}
                    >
                      <i className="bi bi-pencil" style={{ fontSize: '11px' }}></i>
                      <span>Editar</span>
                    </button>
                  </div>
                )}

                {/* Elemento minimizado da programação confirmada */}
                {isScheduled && scheduleData && (
                  <div style={{
                    background: 'var(--modal-bg, #ffffff)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    border: '1px solid var(--border-subtle, #e8e8e8)',
                    boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    position: 'relative'
                  }}>
                    {/* Badge circular com ícone de calendário no canto superior direito */}
                    <div className="open-os-contract-check" style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#7c4dff',
                      border: '2px solid var(--modal-bg, #ffffff)',
                      display: 'flex',
                    alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: '#ffffff',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                      zIndex: 10
                    }}>
                      <i className="bi bi-calendar-check" style={{ fontSize: '12px' }}></i>
                    </div>

                    {/* Lista de informações de programação */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      flex: 1,
                      minWidth: 0
                    }}>
                      {/* Técnico */}
                      <div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 500,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-muted, #718096)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '2px'
                        }}>
                          Técnico
                        </div>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-body, #2d3748)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {scheduleData.technician?.nome || 'N/A'}
                        </div>
                      </div>

                      {/* Data */}
                      <div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 500,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-muted, #718096)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '2px'
                        }}>
                          Data
                        </div>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-body, #2d3748)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {scheduleData.noCertainDate ? 'Não há data certa de previsão' : (scheduleData.date ? formatDateToPTBR(scheduleData.date) : 'N/A')}
                        </div>
                      </div>
                    </div>

                    {/* Botão Editar */}
                        <button className="open-os-btn-secondary"
                          type="button"
                          onClick={() => {
                        // Abrir sidebar novamente com os dados já preenchidos para edição
                        setShowScheduleSidebar(true);
                        setScheduleType(scheduleData.type);
                        setSelectedScheduleDate(scheduleData.date);
                        setScheduleNoCertainDate(!!scheduleData.noCertainDate);
                        setFuturaStandbyNotes(String(scheduleData.standbyNotes || ''));
                        setSelectedTechnician(scheduleData.technician);
                        setIsScheduled(false);
                        setScheduleData(null);
                        // Carregar dados de programação se necessário
                        if (scheduleData.type === 'future' || scheduleData.type === 'apenas_tecnico') {
                          handleLoadScheduleData();
                        }
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        fontWeight: 500,
                        color: 'var(--text-muted, #4a5568)',
                        background: 'var(--bg-surface-alt, #f7fafc)',
                        border: '1px solid var(--border-subtle, #e2e8f0)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                          onMouseEnter={(e) => {
                        e.target.style.background = 'var(--border-subtle, #edf2f7)';
                          }}
                          onMouseLeave={(e) => {
                        e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                      }}
                    >
                      <i className="bi bi-pencil" style={{ fontSize: '11px' }}></i>
                      <span>Editar</span>
                    </button>
                  </div>
                )}

                {/* Versão Minimizada da CONFIRMAÇÃO DE INFORMAÇÕES */}
                <div style={{
                  background: 'var(--modal-bg, #ffffff)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  border: '1px solid var(--border-subtle, #e8e8e8)',
                  boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  position: 'relative'
                }}>
                  {/* Badge circular com ícone de informações no canto superior direito */}
                  <div className="open-os-contract-check" style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: '#7c4dff',
                    border: '2px solid var(--modal-bg, #ffffff)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: '#ffffff',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                    zIndex: 10
                  }}>
                    <i className="bi bi-info-circle" style={{ fontSize: '12px' }}></i>
                  </div>
                  <div style={{
                    flex: 1,
                    minWidth: 0
                  }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-muted, #718096)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '2px'
                    }}>
                      Informações
                    </div>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {selectedEquipment && selectedEquipment.codigo ? selectedEquipment.codigo : '-'}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowLaudo(false)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                      padding: '6px 12px',
                            fontSize: '11px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 500,
                      color: 'var(--text-muted, #4a5568)',
                      background: 'var(--bg-surface-alt, #f7fafc)',
                      border: '1px solid var(--border-subtle, #e2e8f0)',
                            borderRadius: '6px',
                      cursor: 'pointer',
                            transition: 'all 0.2s ease',
                      flexShrink: 0
                    }}
                    className="open-os-btn-secondary"
                    onMouseEnter={(e) => {
                      e.target.style.background = 'var(--border-subtle, #edf2f7)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                    }}
                  >
                    <i className="bi bi-pencil" style={{ fontSize: '11px' }}></i>
                    <span>Editar</span>
                        </button>
                </div>

                {/* Elemento minimizado do TIPO DE OS (modo único) */}
                {tipoOsUniqueConfirmed && osType !== 'estoque' && (
                  <div style={{
                    background: 'var(--modal-bg, #ffffff)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    border: '1px solid var(--border-subtle, #e8e8e8)',
                    boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    position: 'relative',
                    flexShrink: 0,
                    width: '210px'
                  }}>
                    <div style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 500,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-muted, #718096)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        TIPO DE OS
                      </div>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-body, #2d3748)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {getTipoOsLabel(tipoOsUniqueValue)}
                      </div>
                    </div>

                    <button className="open-os-btn-secondary"
                      type="button"
                      onClick={() => setTipoOsUniqueConfirmed(false)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        fontWeight: 500,
                        color: 'var(--text-muted, #4a5568)',
                        background: 'var(--bg-surface-alt, #f7fafc)',
                        border: '1px solid var(--border-subtle, #e2e8f0)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'var(--border-subtle, #edf2f7)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                      }}
                    >
                      <i className="bi bi-pencil" style={{ fontSize: '11px' }}></i>
                      <span>Editar</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Layout normal: ETAPA 1 e INFORMAÇÕES lado a lado */
            <div style={{
              display: 'flex',
              gap: '20px',
              alignItems: 'flex-start',
              flexDirection: (selectedEquipment || (confirmedContractEquipments && contractModeData)) ? 'row-reverse' : 'row'
            }}>
              {/* Coluna à direita com ETAPA 1 minimizada (quando equipamento selecionado) */}
              {confirmedContractEquipments && contractModeData ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  flexShrink: 0
                }}>
                  {/* Contrato Selecionado minimizado */}
                  <div style={{
                    background: 'var(--modal-bg, #ffffff)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    border: '1px solid var(--border-subtle, #e8e8e8)',
                    boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flexShrink: 0,
                    width: '210px'
                  }}>
                    <div style={{
                      flex: 1,
                      minWidth: 0
                    }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 500,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-muted, #718096)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '2px'
                      }}>
                        Contrato Selecionado
                      </div>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-body, #2d3748)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {contractModeData.contract.classif || contractModeData.contract.codigo}
                      </div>
                    </div>
                  </div>
                  
                  {/* Equipamentos Selecionados minimizado */}
                  <div style={{
                    background: 'var(--modal-bg, #ffffff)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    border: '1px solid var(--border-subtle, #e8e8e8)',
                    boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    flexShrink: 0,
                    width: '210px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: '#7c4dff'
                      }}>
                        {contractModeData.equipments.length} {contractModeData.equipments.length === 1 ? 'equipamento selecionado' : 'equipamentos selecionados'}
                      </div>
                      <button className="open-os-btn-secondary"
                        onClick={() => {
                          // Restaurar os equipamentos selecionados antes de voltar à seleção
                          setSelectedEquipments(contractModeData.equipments);
                          setConfirmedContractEquipments(null);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          padding: '6px 12px',
                          fontSize: '11px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          fontWeight: 500,
                          color: 'var(--text-muted, #4a5568)',
                          background: 'var(--bg-surface-alt, #f7fafc)',
                          border: '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'var(--border-subtle, #edf2f7)';
                          e.target.style.borderColor = 'var(--border-subtle, #cbd5e0)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                          e.target.style.borderColor = 'var(--border-subtle, #e2e8f0)';
                        }}
                      >
                        <i className="bi bi-pencil" style={{ fontSize: '11px' }}></i>
                        <span>Editar</span>
                      </button>
                    </div>
                    {contractModeData.tipoOs && (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px'
                      }}>
                        <label style={{
                          fontSize: '11px',
                          fontWeight: 500,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-muted, #718096)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Tipo
                        </label>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 500,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-body, #2d3748)'
                        }}>
                          {getTipoOsLabel(contractModeData.tipoOs)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Elemento minimizado da programação confirmada (modo contrato) */}
                  {isScheduled && scheduleData && (
                    <div style={{
                      background: 'var(--modal-bg, #ffffff)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      border: '1px solid var(--border-subtle, #e8e8e8)',
                      boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      position: 'relative',
                      flexShrink: 0,
                      width: '210px'
                    }}>
                      {/* Badge circular com ícone de calendário no canto superior direito */}
                      <div className="open-os-contract-check" style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#7c4dff',
                        border: '2px solid var(--modal-bg, #ffffff)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                        zIndex: 10
                      }}>
                        <i className="bi bi-calendar-check" style={{ fontSize: '12px' }}></i>
                      </div>

                      {/* Lista de informações de programação */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        flex: 1,
                        minWidth: 0
                      }}>
                        {/* Técnico */}
                        <div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: 500,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-muted, #718096)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '2px'
                          }}>
                            Técnico
                          </div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-body, #2d3748)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {scheduleData.technician?.nome || 'N/A'}
                          </div>
                        </div>

                        {/* Data */}
                        <div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: 500,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-muted, #718096)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '2px'
                          }}>
                            Data
                          </div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-body, #2d3748)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {scheduleData.noCertainDate ? 'Não há data certa de previsão' : (scheduleData.date ? formatDateToPTBR(scheduleData.date) : 'N/A')}
                          </div>
                        </div>
                      </div>

                      {/* Botão Editar */}
                      <button className="open-os-btn-secondary"
                        type="button"
                        onClick={() => {
                          // Abrir sidebar novamente com os dados já preenchidos para edição
                          setShowScheduleSidebar(true);
                          setScheduleType(scheduleData.type);
                          setSelectedScheduleDate(scheduleData.date);
                          setScheduleNoCertainDate(!!scheduleData.noCertainDate);
                          setFuturaStandbyNotes(String(scheduleData.standbyNotes || ''));
                          setSelectedTechnician(scheduleData.technician);
                          setIsScheduled(false);
                          setScheduleData(null);
                          // Carregar dados de programação se necessário
                          if (scheduleData.type === 'future' || scheduleData.type === 'apenas_tecnico') {
                            handleLoadScheduleData();
                          }
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          padding: '6px 12px',
                          fontSize: '11px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          fontWeight: 500,
                          color: 'var(--text-muted, #4a5568)',
                          background: 'var(--bg-surface-alt, #f7fafc)',
                          border: '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'var(--border-subtle, #edf2f7)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                        }}
                      >
                        <i className="bi bi-pencil" style={{ fontSize: '11px' }}></i>
                        <span>Editar</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : selectedEquipment ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  flexShrink: 0
                }}>
                  {/* Versão Compacta da ETAPA 1 (quando equipamento selecionado) */}
                  <div style={{
                    background: 'var(--modal-bg, #ffffff)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    border: '1px solid var(--border-subtle, #e8e8e8)',
                    boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flexShrink: 0,
                    position: 'relative'
                  }}>
                    {/* Badge circular com ícone de equipamento no canto superior direito */}
                          <div className="open-os-contract-check" style={{
                            position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#7c4dff',
                      border: '2px solid var(--modal-bg, #ffffff)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: '#ffffff',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                      zIndex: 10
                    }}>
                      <i className="bi bi-printer" style={{ fontSize: '12px' }}></i>
                    </div>
                    <div style={{
                      flex: 1,
                      minWidth: 0
                    }}>
                      <div style={{
                            fontSize: '11px',
                        fontWeight: 500,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-muted, #718096)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '2px'
                      }}>
                        {osType === 'contrato' ? 'Contrato' : 'Estoque'}
                            </div>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-body, #2d3748)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {selectedEquipment.serie}
                            </div>
                          </div>
                    <button className="open-os-btn-secondary"
                      onClick={() => {
                        setSelectedEquipment(null);
                        setLastOsDate(null);
                        setLastOsData(null);
                        setShowDefeitoDetails(false);
                        setShowLaudo(false);
                        setShowLastOsSection(false);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        fontWeight: 500,
                        color: 'var(--text-muted, #4a5568)',
                        background: 'var(--bg-surface-alt, #f7fafc)',
                        border: '1px solid var(--border-subtle, #e2e8f0)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'var(--border-subtle, #edf2f7)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                      }}
                    >
                      <i className="bi bi-pencil" style={{ fontSize: '11px' }}></i>
                      <span>Editar</span>
                    </button>
                  </div>

                  {/* Elemento minimizado dos contadores confirmados */}
                  {countersConfirmed && showCounters && (
                          <div style={{
                            background: 'var(--modal-bg, #ffffff)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      border: '1px solid var(--border-subtle, #e8e8e8)',
                      boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      position: 'relative'
                    }}>
                      {/* Badge circular com ícone de medidor no canto superior direito */}
                            <div className="open-os-contract-check" style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#7c4dff',
                        border: '2px solid var(--modal-bg, #ffffff)',
                              display: 'flex',
                              alignItems: 'center',
                        justifyContent: 'center',
                              fontSize: '12px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                        zIndex: 10
                      }}>
                        <i className="bi bi-speedometer2" style={{ fontSize: '12px' }}></i>
                            </div>

                      {/* Lista de contadores */}
                            <div style={{
                              display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        flex: 1,
                        minWidth: 0
                      }}>
                        {/* Contador P&B */}
                        <div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: 500,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-muted, #718096)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '2px'
                          }}>
                            Contador P&B
                          </div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              color: 'var(--text-body, #2d3748)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {formatNumberWithMask(counterValues?.pb || '0')}
                          </div>
                        </div>

                        {/* Contador Cor */}
                        <div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: 500,
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-muted, #718096)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '2px'
                          }}>
                            Contador Cor
                            </div>
                            <div style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-body, #2d3748)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {formatNumberWithMask(counterValues?.color || '0')}
                          </div>
                        </div>

                        {/* Contador Total */}
                        <div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: 500,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-muted, #718096)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '2px'
                          }}>
                            Contador Total
                          </div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-body, #2d3748)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {formatNumberWithMask(counterValues?.total || '0')}
                          </div>
                        </div>
                      </div>

                      {/* Botão Editar */}
                      <button className="open-os-btn-secondary"
                        type="button"
                        onClick={() => {
                          // Permite editar novamente os contadores
                          setCountersConfirmed(false);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          padding: '6px 12px',
                          fontSize: '11px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          fontWeight: 500,
                          color: 'var(--text-muted, #4a5568)',
                          background: 'var(--bg-surface-alt, #f7fafc)',
                          border: '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '6px',
                              cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'var(--border-subtle, #edf2f7)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                        }}
                      >
                        <i className="bi bi-pencil" style={{ fontSize: '11px' }}></i>
                        <span>Editar</span>
                      </button>
                    </div>
                  )}

                  {/* Elemento minimizado da programação confirmada */}
                  {isScheduled && scheduleData && (
                    <div style={{
                      background: 'var(--modal-bg, #ffffff)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      border: '1px solid var(--border-subtle, #e8e8e8)',
                      boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      position: 'relative'
                    }}>
                      {/* Badge circular com ícone de calendário no canto superior direito */}
                      <div className="open-os-contract-check" style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#7c4dff',
                        border: '2px solid var(--modal-bg, #ffffff)',
                              display: 'flex',
                              alignItems: 'center',
                        justifyContent: 'center',
                              fontSize: '12px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                        zIndex: 10
                      }}>
                        <i className="bi bi-calendar-check" style={{ fontSize: '12px' }}></i>
                            </div>

                      {/* Lista de informações de programação */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        flex: 1,
                        minWidth: 0
                      }}>
                        {/* Técnico */}
                        <div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: 500,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-muted, #718096)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '2px'
                          }}>
                            Técnico
                          </div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-body, #2d3748)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {scheduleData.technician?.nome || 'N/A'}
                          </div>
                      </div>

                        {/* Data */}
                        <div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: 500,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-muted, #718096)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '2px'
                          }}>
                            Data
                          </div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-body, #2d3748)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {scheduleData.noCertainDate ? 'Não há data certa de previsão' : (scheduleData.date ? formatDateToPTBR(scheduleData.date) : 'N/A')}
                          </div>
                        </div>
                      </div>

                      {/* Botão Editar */}
                      <button className="open-os-btn-secondary"
                        type="button"
                        onClick={() => {
                          // Abrir sidebar novamente com os dados já preenchidos para edição
                          setShowScheduleSidebar(true);
                          setScheduleType(scheduleData.type);
                          setSelectedScheduleDate(scheduleData.date);
                          setScheduleNoCertainDate(!!scheduleData.noCertainDate);
                          setFuturaStandbyNotes(String(scheduleData.standbyNotes || ''));
                          setSelectedTechnician(scheduleData.technician);
                          setIsScheduled(false);
                          setScheduleData(null);
                          // Carregar dados de programação se necessário
                          if (scheduleData.type === 'future' || scheduleData.type === 'apenas_tecnico') {
                            handleLoadScheduleData();
                          }
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          padding: '6px 12px',
                          fontSize: '11px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          fontWeight: 500,
                          color: 'var(--text-muted, #4a5568)',
                          background: 'var(--bg-surface-alt, #f7fafc)',
                          border: '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'var(--border-subtle, #edf2f7)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                        }}
                      >
                        <i className="bi bi-pencil" style={{ fontSize: '11px' }}></i>
                        <span>Editar</span>
                      </button>
                    </div>
                  )}
                  </div>
              ) : selectedContract ? (
                /* Layout quando contrato selecionado: Seleção de Equipamentos à esquerda + Etapa 1 minimizada à direita */
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                  width: '100%'
                }}>
                  {/* Seleção de Equipamentos */}
                  <div style={{ 
                    flex: '1 1 auto', 
                    minWidth: 0,
                    maxWidth: 'calc(100% - 225px)'
                  }}>
                    <EquipmentSelectionStep
                      contrato={selectedContract.codigo}
                      selectedEquipments={selectedEquipments}
                      setSelectedEquipments={setSelectedEquipments}
                      onEquipmentsSelected={(equipments) => {
                        // Quando equipamentos são selecionados, podemos processar
                      }}
                      onConfirmEquipments={(equipments, tipoOs) => {
                        // Quando equipamentos são confirmados (após validação), preparar para exibir no EquipmentInfoStep
                        setConfirmedContractEquipments({
                          equipments: equipments,
                          contract: selectedContract,
                          tipoOs: tipoOs
                        });
                        // O EquipmentInfoStep será renderizado na próxima etapa
                      }}
                    />
                  </div>

                  {/* Etapa 1 Minimizada */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    flexShrink: 0,
                    width: '213px',
                    alignSelf: 'flex-start'
                  }}>
                    <div style={{
                      background: 'var(--modal-bg, #ffffff)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      border: '1px solid var(--border-subtle, #e8e8e8)',
                      boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        flex: 1,
                        minWidth: 0
                      }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 500,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-muted, #718096)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '2px'
                        }}>
                          Contrato Selecionado
                        </div>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-body, #2d3748)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {selectedContract.classif || selectedContract.codigo}
                        </div>
                      </div>
                      <button className="open-os-btn-secondary"
                        onClick={() => {
                          setSelectedContract(null);
                          setSelectedEquipments([]);
                          setSelectedEquipment(null);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          padding: '6px 12px',
                          fontSize: '11px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          fontWeight: 500,
                          color: 'var(--text-muted, #4a5568)',
                          background: 'var(--bg-surface-alt, #f7fafc)',
                          border: '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'var(--border-subtle, #edf2f7)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                        }}
                      >
                        <i className="bi bi-pencil" style={{ fontSize: '11px' }}></i>
                        <span>Editar</span>
                      </button>
                    </div>

                    {/* Equipamentos Selecionados */}
                    {selectedEquipments.length > 0 && (
                      <div style={{
                        background: 'var(--modal-bg, #ffffff)',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        border: '1px solid var(--border-subtle, #e8e8e8)',
                        boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: selectedEquipments.length > 0 ? '#7c4dff' : 'var(--text-muted, #718096)'
                        }}>
                          {selectedEquipments.length} {selectedEquipments.length === 1 ? 'equipamento selecionado' : 'equipamentos selecionados'}
                        </div>
                        <button
                          onClick={() => {
                            setSelectedEquipments([]);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            fontSize: '11px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            fontWeight: 500,
                            color: 'var(--text-muted, #4a5568)',
                            background: 'var(--bg-surface-alt, #f7fafc)',
                            border: '1px solid var(--border-subtle, #e2e8f0)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'var(--border-subtle, #edf2f7)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                          }}
                        >
                          <i className="bi bi-x-circle" style={{ fontSize: '11px' }}></i>
                          <span>Limpar</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Etapa 1: Identificação do Equipamento (versão completa quando não há equipamento selecionado) */
                <EquipmentStep
                  osType={osType}
                  setOsType={setOsType}
                  selectedEquipment={selectedEquipment}
                  setSelectedEquipment={setSelectedEquipment}
                  onEquipmentSelect={handleEquipmentSelect}
                  onEquipmentGuardBlocked={(equipment) => {
                    setPendingEquipmentOpenOS(equipment || null);
                  }}
                  setShowOpenOsNotification={setShowOpenOsNotification}
                  setOpenOsNumbers={setOpenOsNumbers}
                  setShowRetiradaAtivaNotification={setShowRetiradaAtivaNotification}
                  setRetiradaAtivaCodigos={setRetiradaAtivaCodigos}
                />
              )}

              {/* Etapa 2: Informações do Equipamento */}
              {(selectedEquipment && !selectedContract) || (confirmedContractEquipments && contractModeData) ? (
                <EquipmentInfoStep
                  selectedEquipment={confirmedContractEquipments && contractModeData ? contractModeData.equipmentData : selectedEquipment}
                  setSelectedEquipment={confirmedContractEquipments && contractModeData ? () => {} : setSelectedEquipment}
                  lastOsDate={confirmedContractEquipments && contractModeData ? null : lastOsDate}
                  lastOsData={confirmedContractEquipments && contractModeData ? null : lastOsData}
                  isLoadingLastOs={confirmedContractEquipments && contractModeData ? false : isLoadingLastOs}
                  showLaudo={showLaudo}
                  setShowLaudo={setShowLaudo}
                  onEquipmentUpdate={confirmedContractEquipments && contractModeData ? () => {} : handleEquipmentUpdate}
                  showDefeitoDetails={showDefeitoDetails}
                  setShowDefeitoDetails={setShowDefeitoDetails}
                  showLastOsSection={confirmedContractEquipments && contractModeData ? false : showLastOsSection}
                  setShowLastOsSection={setShowLastOsSection}
                  // Props para SOLICITANTE, CONTATO, EMAIL, DEFEITO
                  solicitante={solicitante}
                  setSolicitante={setSolicitante}
                  contatoFone1={contatoFone1}
                  setContatoFone1={setContatoFone1}
                  contatoWhatsApp={contatoWhatsApp}
                  setContatoWhatsApp={setContatoWhatsApp}
                  contatoFone2={contatoFone2}
                  setContatoFone2={setContatoFone2}
                  email={email}
                  setEmail={setEmail}
                  emailError={emailError}
                  setEmailError={setEmailError}
                  selectedDefeito={selectedDefeito}
                  setSelectedDefeito={setSelectedDefeito}
                  defeitoObservacao={defeitoObservacao}
                  setDefeitoObservacao={setDefeitoObservacao}
                  // Props para Contadores
                  showCounters={showCounters}
                  setShowCounters={setShowCounters}
                  countersConfirmed={countersConfirmed}
                  setCountersConfirmed={setCountersConfirmed}
                  loadingCounters={loadingCounters}
                  setLoadingCounters={setLoadingCounters}
                  lastCounters={lastCounters}
                  setLastCounters={setLastCounters}
                  counterValues={counterValues}
                  setCounterValues={setCounterValues}
                  counterErrors={counterErrors}
                  setCounterErrors={setCounterErrors}
                  // Props para botões de ação
                  validateRequiredFields={validateRequiredFields}
                  isScheduled={isScheduled}
                  scheduleData={scheduleData}
                  showScheduleTooltip={showScheduleTooltip}
                  setShowScheduleTooltip={setShowScheduleTooltip}
                  showScheduleDropdown={showScheduleDropdown}
                  setShowScheduleDropdown={setShowScheduleDropdown}
                  showScheduleSidebar={showScheduleSidebar}
                  setShowScheduleSidebar={setShowScheduleSidebar}
                  scheduleType={scheduleType}
                  setScheduleType={setScheduleType}
                  selectedScheduleDate={selectedScheduleDate}
                  setSelectedScheduleDate={setSelectedScheduleDate}
                  setScheduleNoCertainDate={setScheduleNoCertainDate}
                  setFuturaStandbyNotes={setFuturaStandbyNotes}
                  handleLoadScheduleData={handleLoadScheduleData}
                  handleCreateOS={handleCreateOS}
                  isCreatingOS={isCreatingOS}
                  osType={osType}
                  isContractMode={confirmedContractEquipments && contractModeData ? true : false}
                  contractClassif={confirmedContractEquipments && contractModeData ? contractModeData.contract.classif : undefined}
                  contractEndereco={confirmedContractEquipments && contractModeData ? contractModeData.equipmentData.contractEndereco : undefined}
                  hasMultipleSites={confirmedContractEquipments && contractModeData ? contractModeData.hasMultipleSites : false}
                  tipoOsUniqueValue={tipoOsUniqueValue}
                  setTipoOsUniqueValue={setTipoOsUniqueValue}
                  tipoOsUniqueConfirmed={tipoOsUniqueConfirmed}
                  setTipoOsUniqueConfirmed={setTipoOsUniqueConfirmed}
                  selectedEquipments={confirmedContractEquipments && contractModeData ? contractModeData.equipments : undefined}
                  defeitoVinculoEquipAtivo={defeitoEquipVinculoAtivo}
                  tipoOsValidationExtras={tipoOsValidationExtras}
                />
              ) : null}

              {/* Componentes removidos - agora renderizados dentro do EquipmentInfoStep */}
              {/* DefeitoFormStep, CountersStep e botões de ação foram movidos para dentro do EquipmentInfoStep */}
            </div>
          )}
        </div>
      </div>

      {/* Overlay quando barra lateral está aberta */}
      {showScheduleSidebar && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          animation: 'fadeIn 0.2s ease',
          pointerEvents: 'auto'
          // Removido onClick - sidebar só fecha pelo botão "x"
        }} />
      )}

      {/* Barra lateral de programação de atendimento */}
      {showScheduleSidebar && (
        <div
          className="open-os-schedule-sidebar"
          style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '280px',
          height: '100vh',
          background: 'var(--modal-bg, #ffffff)',
          borderRight: '1px solid var(--border-subtle, #e2e8f0)',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInLeft 0.3s ease'
        }}>
          <style>{`
            @keyframes slideInLeft {
              from { transform: translateX(-100%); }
              to { transform: translateX(0); }
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>

          {/* Header da barra lateral */}
          <div style={{
            padding: '10px',
            borderBottom: '1px solid var(--border-subtle, #e2e8f0)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'var(--bg-surface-alt, #fafbfc)'
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 600,
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              color: 'var(--text-body, #2d3748)'
            }}>
              Programar Atendimento
            </h3>
            <button
              type="button"
              onClick={() => {
                setShowScheduleSidebar(false);
    setScheduleType(null);
    setSelectedScheduleDate(null);
    setScheduleNoCertainDate(false);
    setSelectedTechnician(null);
    setExpandedAreas(new Set());
              }}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '20px',
                color: 'var(--text-muted, #64748b)',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'var(--bg-surface-alt, #f1f5f9)';
                e.target.style.color = 'var(--text-body, #334155)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = 'var(--text-muted, #64748b)';
              }}
            >
              ×
            </button>
          </div>

          {/* Conteúdo da barra lateral */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px 0'
          }}>
            {/* Lista de áreas e técnicos */}
            <div>
              {!isLoadingScheduleData && scheduleAreasData.length > 0 && (
                <div style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  color: 'var(--text-muted, #9ca3af)',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  marginTop: '0',
                  marginBottom: '6px',
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="bi bi-person-check" style={{ fontSize: '14px', color: 'var(--text-muted, #9ca3af)' }}></i>
                  <span>Selecione o técnico</span>
                </div>
              )}

              {isLoadingScheduleData ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '40px',
                  color: '#7c4dff'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #7c4dff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }}></div>
                </div>
              ) : scheduleAreasData.length === 0 ? (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: 'var(--text-muted, #718096)',
                  fontSize: '13px',
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif"
                }}>
                  Nenhuma área disponível
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {scheduleAreasData.map((area) => {
                    const isExpanded = expandedAreas.has(area.id);
                    return (
                      <div key={area.id} style={{ marginBottom: '2px' }}>
                        {/* Área */}
                        <div style={{
                          padding: '6px 10px',
                          background: 'var(--bg-surface-alt, #f7fafc)',
                          borderRadius: '0',
                          marginBottom: '0',
                          fontSize: '12px',
                          fontWeight: 600,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: '#7c4dff',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          width: '100%'
                        }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newExpanded = new Set(expandedAreas);
                              if (isExpanded) {
                                newExpanded.delete(area.id);
                              } else {
                                newExpanded.add(area.id);
                              }
                              setExpandedAreas(newExpanded);
                            }}
                            style={{
                              background: 'var(--modal-bg, #ffffff)',
                              border: '1.5px solid #7c4dff',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#7c4dff',
                              fontSize: '7px',
                              fontWeight: 600,
                              transition: 'all 0.2s ease',
                              width: '10px',
                              height: '10px',
                              minWidth: '10px',
                              flexShrink: 0
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#7c4dff';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'var(--modal-bg, #ffffff)';
                              e.target.style.color = '#7c4dff';
                              e.target.style.transform = 'scale(1)';
                            }}
                          >
                            {isExpanded ? '−' : '+'}
                          </button>
                          <span style={{ flex: 1 }}>{area.nome || area.name}</span>
                        </div>
                        {/* Técnicos da área */}
                        {isExpanded && (
                          <div style={{ paddingLeft: '22px', paddingTop: '2px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {area.tecnicos.map((tecnico) => (
                              <div
                                key={tecnico.id}
                                className="open-os-tech-item"
                                onClick={() => {
                                  const scheduleDate = scheduleType === 'today' 
                                    ? getTodayLocal()
                                    : scheduleType === 'tomorrow'
                                    ? getLocalDateString(getNextBusinessDay())
                                    : scheduleType === 'apenas_tecnico'
                                    ? getTodayLocal()
                                    : selectedScheduleDate;
                                  
                                  setSelectedTechnician(tecnico);
                                  
                                  if (scheduleType === 'future') {
                                    if (selectedScheduleDate || scheduleNoCertainDate) {
                                      if (scheduleNoCertainDate) {
                                        const notes = String(futuraStandbyNotes || '').trim();
                                        if (notes.length < 10) {
                                          alert('Informe observações sobre o agendamento (mínimo 10 caracteres).');
                                          return;
                                        }
                                      }
                                      setScheduleData({
                                        type: scheduleType,
                                        date: scheduleNoCertainDate ? null : scheduleDate,
                                        technician: tecnico,
                                        noCertainDate: scheduleNoCertainDate,
                                        standbyNotes: scheduleNoCertainDate
                                          ? String(futuraStandbyNotes).trim()
                                          : undefined
                                      });
                                      setIsScheduled(true);
                                      setShowScheduleSidebar(false);
                                    }
                                  } else if (scheduleType === 'apenas_tecnico') {
                                    setScheduleData({
                                      type: 'apenas_tecnico',
                                      date: getTodayLocal(),
                                      technician: tecnico
                                    });
                                    setIsScheduled(true);
                                    setShowScheduleSidebar(false);
                                  } else {
                                    setScheduleData({
                                      type: scheduleType,
                                      date: scheduleDate,
                                      technician: tecnico
                                    });
                                    setIsScheduled(true);
                                    setShowScheduleSidebar(false);
                                  }
                                }}
                                style={{
                                  padding: '6px 10px',
                                  background: selectedTechnician?.id === tecnico.id ? 'rgba(124, 77, 255, 0.16)' : 'var(--bg-surface-alt, #f7fafc)',
                                  border: selectedTechnician?.id === tecnico.id ? '1px solid #7c4dff' : '1px solid var(--border-subtle, #e2e8f0)',
                                  borderRadius: '0',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  fontSize: '12px',
                                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                  color: selectedTechnician?.id === tecnico.id ? '#7c4dff' : 'var(--text-body, #2d3748)',
                                  fontWeight: selectedTechnician?.id === tecnico.id ? 500 : 400,
                                  marginLeft: '0',
                                  marginRight: '0',
                                  width: 'calc(100% - 22px)'
                                }}
                                onMouseEnter={(e) => {
                                  if (selectedTechnician?.id !== tecnico.id) {
                                    e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                                    e.target.style.borderColor = 'var(--border-subtle, #cbd5e0)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (selectedTechnician?.id !== tecnico.id) {
                                    e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                                    e.target.style.borderColor = 'var(--border-subtle, #e2e8f0)';
                                  }
                                }}
                              >
                                {tecnico.nome}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Date picker para previsão de atendimento (oculto em "Apenas definir técnico") */}
              {!isLoadingScheduleData && scheduleAreasData.length > 0 && scheduleType !== 'apenas_tecnico' && (
                <>
                  <div style={{
                    padding: '6px 12px',
                    background: 'transparent',
                    color: 'var(--text-muted, #9ca3af)',
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    marginTop: '16px',
                    marginBottom: '6px',
                    borderRadius: '0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <i className="bi bi-calendar3" style={{ fontSize: '14px', color: 'var(--text-muted, #9ca3af)' }}></i>
                    <span>Previsão de atendimento</span>
                  </div>
                  <div style={{ padding: '0 12px', marginBottom: '12px' }}>
                    <input
                      type="date"
                      lang="pt-BR"
                      className="open-os-schedule-date"
                      value={selectedScheduleDate ? getLocalDateString(selectedScheduleDate) : ''}
                      min={scheduleType === 'future' ? getLocalDateString(getDayAfterNextBusinessDay()) : getTodayLocal()}
                      disabled={scheduleType === 'today' || scheduleType === 'tomorrow' || scheduleNoCertainDate}
                      onChange={(e) => {
                        if (scheduleType === 'future') {
                          const dateValue = e.target.value;
                          if (dateValue) {
                            setSelectedScheduleDate(dateValue);
                            
                            // Se o técnico já estiver selecionado, confirmar e fechar automaticamente
                            if (selectedTechnician) {
                              setScheduleData({
                                type: scheduleType,
                                date: dateValue,
                                technician: selectedTechnician
                              });
                              setIsScheduled(true);
                              setShowScheduleSidebar(false);
                            }
                          } else {
                            setSelectedScheduleDate(null);
                          }
                        }
                      }}
                      onClick={(e) => {
                        // Garantir que o calendário abra ao clicar no campo (apenas para agendamento futuro)
                        if (scheduleType === 'future' && !scheduleNoCertainDate && !e.target.value && e.target.showPicker) {
                          try {
                            e.target.showPicker();
                          } catch (error) {
                            console.log('showPicker não disponível, usando comportamento padrão');
                          }
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        border: selectedScheduleDate ? '2px solid #7c4dff' : '1px solid var(--border-subtle, #e2e8f0)',
                        borderRadius: '4px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        color: (scheduleType === 'today' || scheduleType === 'tomorrow' || scheduleNoCertainDate) ? 'var(--text-muted, #6b7280)' : 'var(--text-body, #2d3748)',
                        background: (scheduleType === 'today' || scheduleType === 'tomorrow' || scheduleNoCertainDate) ? 'var(--bg-surface-alt, #f3f4f6)' : (selectedScheduleDate ? 'var(--bg-surface-alt, #f3f4f6)' : 'var(--input-bg, #ffffff)'),
                        cursor: (scheduleType === 'today' || scheduleType === 'tomorrow' || scheduleNoCertainDate) ? 'not-allowed' : 'pointer',
                        opacity: (scheduleType === 'today' || scheduleType === 'tomorrow' || scheduleNoCertainDate) ? 0.7 : 1,
                        position: 'relative',
                        zIndex: 1
                      }}
                      onFocus={(e) => {
                        if (scheduleType !== 'today' && scheduleType !== 'tomorrow' && !scheduleNoCertainDate) {
                          e.target.style.borderColor = '#7c4dff';
                          e.target.style.boxShadow = '0 0 0 3px rgba(124, 77, 255, 0.1)';
                        }
                      }}
                      onBlur={(e) => {
                        if (!selectedScheduleDate) {
                          e.target.style.borderColor = 'var(--border-subtle, #e2e8f0)';
                        }
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    {scheduleType === 'future' && (
                      <div className="future-date-checkbox-container" style={{ marginTop: '8px' }}>
                        <label className="future-date-checkbox-label">
                          <input
                            type="checkbox"
                            checked={scheduleNoCertainDate}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setScheduleNoCertainDate(checked);
                              if (checked) {
                                setSelectedScheduleDate(null);
                              } else {
                                setFuturaStandbyNotes('');
                              }
                            }}
                          />
                          <span>Não há data certa de previsão</span>
                        </label>
                      </div>
                    )}
                    {scheduleType === 'future' && scheduleNoCertainDate && (
                      <div style={{ marginTop: '10px', padding: '0 12px', marginBottom: '12px' }}>
                        <label
                          htmlFor="open-os-futura-standby-notes"
                          style={{
                            display: 'block',
                            fontSize: '11px',
                            color: 'var(--text-muted, #4a5568)',
                            marginBottom: '6px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            fontWeight: 500
                          }}
                        >
                          Observações sobre a previsão <span style={{ color: '#c53030' }}>*</span> (mín. 10 caracteres)
                        </label>
                        <textarea
                          id="open-os-futura-standby-notes"
                          value={futuraStandbyNotes}
                          onChange={(e) => setFuturaStandbyNotes(e.target.value)}
                          rows={3}
                          placeholder="Descreva o contexto do agendamento futuro…"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '12px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            border: `1px solid ${
                              String(futuraStandbyNotes).trim().length > 0 &&
                              String(futuraStandbyNotes).trim().length < 10
                                ? '#f56565'
                                : 'var(--border-subtle, #e2e8f0)'
                            }`,
                            borderRadius: '4px',
                            resize: 'vertical',
                            boxSizing: 'border-box',
                            color: 'var(--text-body, #2d3748)'
                          }}
                        />
                        {String(futuraStandbyNotes).trim().length > 0 &&
                        String(futuraStandbyNotes).trim().length < 10 ? (
                          <div
                            style={{
                              fontSize: '10px',
                              color: '#c53030',
                              marginTop: '4px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif"
                            }}
                          >
                            Faltam {10 - String(futuraStandbyNotes).trim().length} caracteres.
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overlay de loading durante criação da OS */}
      {isCreatingOS && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 20000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px'
        }}>
          <div style={{
            fontSize: '16px',
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: '#ffffff',
            fontWeight: 500,
            textAlign: 'center'
          }}>
            Aguarde, estamos gerando sua ordem de serviço.
          </div>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid #ffffff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Modal de progresso durante criação de múltiplas OS */}
      {isCreatingMultipleOS && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 20000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--modal-bg, #ffffff)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                border: '3px solid #7c4dff',
                borderTop: '3px solid transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }}></div>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  color: 'var(--text-body, #2d3748)'
                }}>
                  Criando Ordens de Serviço
                </h3>
                <div style={{
                  fontSize: '12px',
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  color: 'var(--text-muted, #718096)',
                  marginTop: '4px'
                }}>
                  {multipleOSProgress.current} de {multipleOSProgress.total}
                </div>
              </div>
            </div>
            {multipleOSProgress.currentSerie && (
              <div style={{
                fontSize: '12px',
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: 'var(--text-muted, #4a5568)',
                marginTop: '8px',
                padding: '8px',
                background: 'var(--bg-surface-alt, #f7fafc)',
                borderRadius: '6px'
              }}>
                Processando: {multipleOSProgress.currentSerie}
              </div>
            )}
            <div style={{
              width: '100%',
              height: '6px',
              background: 'var(--border-subtle, #e2e8f0)',
              borderRadius: '3px',
              marginTop: '16px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(multipleOSProgress.current / multipleOSProgress.total) * 100}%`,
                height: '100%',
                background: '#7c4dff',
                transition: 'width 0.3s ease',
                borderRadius: '3px'
              }}></div>
            </div>
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Modal de resultado da criação da OS */}
      {showOSResultModal && (osResult || (multipleOSResults && multipleOSResults.length > 0)) && (
        <div className="open-os-result-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 20001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        // Removido onClick - modal só fecha pelo botão "Fechar"
        >
          <div className="open-os-result-modal" style={{
            background: 'var(--modal-bg, #ffffff)',
            borderRadius: '12px',
            padding: (multipleOSResults && multipleOSResults.length > 0) ? '24px' : (osResult && osResult.success ? '16px' : '24px'),
            maxWidth: (multipleOSResults && multipleOSResults.length > 0) ? '700px' : '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }}
          onClick={(e) => e.stopPropagation()}>
            {/* Verificar se é modo múltiplo (contrato) ou modo único */}
            {multipleOSResults && multipleOSResults.length > 0 ? (
              /* Modal de resultados múltiplos (modo contrato) */
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px'
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: 600,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-body, #2d3748)'
                  }}>
                    Resultado da Criação de OS
                  </h3>
                  <button
                    onClick={() => {
                      setShowOSResultModal(false);
                      // Verificar se houve programação para atualizar a coluna correta
                      const hasScheduledOS = multipleOSResults.length > 0 && multipleOSResults[0]._scheduled;
                      const technicianId = multipleOSResults.length > 0 ? multipleOSResults[0]._technicianId : null;
                      const hasSuccess = multipleOSResults.some(r => r.success);
                      
                      if (hasSuccess && onOSCreated) {
                        onOSCreated({
                          scheduled: hasScheduledOS || false,
                          technicianId: technicianId || null
                        });
                      }
                      setMultipleOSResults([]);
                      handleClose();
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '20px',
                      color: 'var(--text-muted, #718096)',
                      cursor: 'pointer',
                      padding: '0',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </div>

                {/* Estatísticas */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '20px'
                }}>
                  <div className="open-os-result-success" style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f0fdf4',
                    borderRadius: '8px',
                    border: '1px solid #86efac'
                  }}>
                    <div style={{
                      fontSize: '11px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: '#166534',
                      marginBottom: '4px',
                      fontWeight: 500
                    }}>
                      Sucesso
                    </div>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: 700,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: '#166534'
                    }}>
                      {multipleOSResults.filter(r => r.success).length}
                    </div>
                  </div>
                  {multipleOSResults.filter(r => !r.success).length > 0 && (
                    <div className="open-os-result-error" style={{
                      flex: 1,
                      padding: '12px',
                      background: '#fef2f2',
                      borderRadius: '8px',
                      border: '1px solid #fecaca'
                    }}>
                      <div style={{
                        fontSize: '11px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: '#991b1b',
                        marginBottom: '4px',
                        fontWeight: 500
                      }}>
                        Erros
                      </div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: '#991b1b'
                      }}>
                        {multipleOSResults.filter(r => !r.success).length}
                      </div>
                    </div>
                  )}
                </div>

                {/* Lista de resultados */}
                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  marginBottom: '16px'
                }}>
                  {multipleOSResults.map((result, index) => (
                    <div
                      key={index}
                      className={result.success ? 'open-os-result-success' : 'open-os-result-error'}
                      style={{
                        padding: '12px',
                        background: result.success ? '#f0fdf4' : '#fef2f2',
                        borderRadius: '8px',
                        marginBottom: index < multipleOSResults.length - 1 ? '8px' : 0,
                        border: `1px solid ${result.success ? '#86efac' : '#fecaca'}`
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {result.success ? (
                            <i className="bi bi-check-circle-fill" style={{ fontSize: '16px', color: '#10b981' }}></i>
                          ) : (
                            <i className="bi bi-x-circle-fill" style={{ fontSize: '16px', color: '#ef4444' }}></i>
                          )}
                          <div>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: 600,
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              color: 'var(--text-body, #2d3748)'
                            }}>
                              {result.serie || result.patrimonio || 'N/A'}
                            </div>
                            {result.patrimonio && result.serie && (
                              <div style={{
                                fontSize: '11px',
                                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                color: 'var(--text-muted, #718096)'
                              }}>
                                Patrimônio: {result.patrimonio}
                              </div>
                            )}
                          </div>
                        </div>
                        {result.success && result.osNumber && (
                          <div style={{
                            fontSize: '13px',
                            fontWeight: 700,
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: '#166534'
                          }}>
                            OS: {result.osNumber}
                          </div>
                        )}
                      </div>
                      {!result.success && result.error && (
                        <div style={{
                          fontSize: '11px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: '#991b1b',
                          marginTop: '6px',
                          padding: '6px',
                          background: 'var(--modal-bg, #ffffff)',
                          borderRadius: '4px'
                        }}>
                          {result.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Botões Opções e Fechar */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  position: 'relative'
                }}>
                  {/* Dropdown Opções */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}
                      style={{
                        padding: '6px 16px',
                        fontSize: '12px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        fontWeight: 600,
                        color: '#ffffff',
                        background: '#5a67d8',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        width: 'auto',
                        minWidth: 'fit-content',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#4c51bf';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#5a67d8';
                      }}
                    >
                      <i className="bi bi-three-dots-vertical" style={{ fontSize: '14px' }}></i>
                      Opções
                    </button>
                    {showOptionsDropdown && (
                      <>
                        <style>{`
                          @keyframes order-impressao-spin { to { transform: rotate(360deg); } }
                          .order-impressao-spin { animation: order-impressao-spin 0.7s linear infinite; }
                        `}</style>
                      <div 
                        data-options-dropdown
                        style={{
                          position: 'absolute',
                          bottom: '100%',
                          right: 0,
                          marginBottom: '8px',
                          background: 'var(--modal-bg, #ffffff)',
                          border: '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          minWidth: '180px',
                          zIndex: 1000,
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden'
                        }}
                      >
                        <button
                          type="button"
                          disabled={!!loadingPdfPrint}
                          onClick={async () => {
                            if (loadingPdfPrint) return;
                            setLoadingPdfPrint('pdf');
                            try {
                            if (confirmedContractEquipments?.equipments && multipleOSResults?.length > 0) {
                                const successfulResults = multipleOSResults.filter(r => r.success && r.osNumber);
                                if (successfulResults.length === 0) return;
                                const equipments = confirmedContractEquipments.equipments;
                                const firstEquipment = equipments[0];
                                const contract = confirmedContractEquipments.contract;
                                const dataFormatada = `${String(new Date().getDate()).padStart(2, '0')}/${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear()}`;
                                let previsaoLabelOverride = null;
                                if (scheduleNoCertainDate) previsaoLabelOverride = 'Não há data certa de previsão';
                                else if (scheduleData?.date) previsaoLabelOverride = formatDateToPTBR(scheduleData.date);
                                else if (scheduleType === 'tomorrow') previsaoLabelOverride = formatDateToPTBR(getNextBusinessDay());
                                else if (scheduleType === 'future' && selectedScheduleDate) previsaoLabelOverride = formatDateToPTBR(selectedScheduleDate);
                                const defeitoNome = selectedDefeito ? (selectedDefeito.nome || selectedDefeito.label || '') : '';
                                const orders = successfulResults.map((result) => {
                                  const equipment = equipments.find(eq => (eq.numSerie && eq.numSerie === result.serie) || (eq.patrimonio && eq.patrimonio === result.patrimonio)) || firstEquipment;
                                  return {
                                    id: result.osNumber,
                                    cliente: equipment.cliente || contract?.classif || '',
                                    endereco: equipment.enderecoCompleto || equipment.Endereco || '',
                                    TB02112_LOCAL: equipment.endereco?.local || equipment.Endereco_Local || '',
                                    equipamento: equipment.modelo || equipment.produto || '',
                                    numeroSerie: equipment.numSerie || equipment.serie || result.serie || '',
                                    patrimonio: equipment.patrimonio || result.patrimonio || '',
                                    dataAbertura: dataFormatada,
                                    solicitante: solicitante || '',
                                    motivoOS: defeitoNome,
                                    motivoOSObservacao: defeitoObservacao || '',
                                    defeitoNome,
                                    defeitoObservacao: defeitoObservacao || '',
                                    tipo: confirmedContractEquipments.tipoOs || '',
                                    TB01007_NOME: equipment.emp_nome || firstEquipment?.emp_nome || '',
                                    TB01007_CNPJ: equipment.emp_cnpj || firstEquipment?.emp_cnpj || '',
                                    TB01007_ENDERECO: equipment.emp_endereco || firstEquipment?.emp_endereco || '',
                                    site_nome: equipment.site_nome || '',
                                    depto_nome: equipment.depto_nome || '',
                                    previsaoLabelOverride: previsaoLabelOverride || undefined
                                  };
                                });
                                await generateOSPdfV2DownloadMultiple(orders);
                              }
                            } finally {
                              setLoadingPdfPrint(null);
                              setShowOptionsDropdown(false);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            fontSize: '12px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            fontWeight: 500,
                            color: loadingPdfPrint ? 'var(--text-muted, #718096)' : 'var(--text-body, #2d3748)',
                            background: loadingPdfPrint === 'pdf' ? 'var(--border-subtle, #edf2f7)' : 'transparent',
                            border: 'none',
                            borderBottom: '1px solid var(--border-subtle, #e2e8f0)',
                            cursor: loadingPdfPrint ? 'wait' : 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => { if (!loadingPdfPrint) e.target.style.background = 'var(--bg-surface-alt, #f7fafc)'; }}
                          onMouseLeave={(e) => { e.target.style.background = loadingPdfPrint === 'pdf' ? 'var(--border-subtle, #edf2f7)' : 'transparent'; }}
                        >
                          {loadingPdfPrint === 'pdf' ? (
                            <>
                              <i className="bi bi-arrow-repeat order-impressao-spin" style={{ fontSize: '14px', color: '#7c4dff' }} aria-hidden />
                              Gerando PDF…
                            </>
                          ) : (
                            <>
                              <i className="bi bi-file-earmark-pdf" style={{ fontSize: '14px', color: '#A6A09B' }}></i>
                              Gerar PDF
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          disabled={!!loadingPdfPrint}
                          onClick={async () => {
                            if (loadingPdfPrint) return;
                            setLoadingPdfPrint('print');
                            try {
                              if (confirmedContractEquipments?.equipments && multipleOSResults?.length > 0) {
                                const successfulResults = multipleOSResults.filter(r => r.success && r.osNumber);
                                if (successfulResults.length === 0) return;
                                const equipments = confirmedContractEquipments.equipments;
                                const firstEquipment = equipments[0];
                                const contract = confirmedContractEquipments.contract;
                                const dataFormatada = `${String(new Date().getDate()).padStart(2, '0')}/${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear()}`;
                                let previsaoLabelOverride = null;
                                if (scheduleNoCertainDate) previsaoLabelOverride = 'Não há data certa de previsão';
                                else if (scheduleData?.date) previsaoLabelOverride = formatDateToPTBR(scheduleData.date);
                                else if (scheduleType === 'tomorrow') previsaoLabelOverride = formatDateToPTBR(getNextBusinessDay());
                                else if (scheduleType === 'future' && selectedScheduleDate) previsaoLabelOverride = formatDateToPTBR(selectedScheduleDate);
                                const defeitoNome = selectedDefeito ? (selectedDefeito.nome || selectedDefeito.label || '') : '';
                                const orders = successfulResults.map((result) => {
                                  const equipment = equipments.find(eq => (eq.numSerie && eq.numSerie === result.serie) || (eq.patrimonio && eq.patrimonio === result.patrimonio)) || firstEquipment;
                                  return {
                                    id: result.osNumber,
                                    cliente: equipment.cliente || contract?.classif || '',
                                    endereco: equipment.enderecoCompleto || equipment.Endereco || '',
                                    TB02112_LOCAL: equipment.endereco?.local || equipment.Endereco_Local || '',
                                    equipamento: equipment.modelo || equipment.produto || '',
                                    numeroSerie: equipment.numSerie || equipment.serie || result.serie || '',
                                    patrimonio: equipment.patrimonio || result.patrimonio || '',
                                    dataAbertura: dataFormatada,
                                    solicitante: solicitante || '',
                                    motivoOS: defeitoNome,
                                    motivoOSObservacao: defeitoObservacao || '',
                                    defeitoNome,
                                    defeitoObservacao: defeitoObservacao || '',
                                    tipo: confirmedContractEquipments.tipoOs || '',
                                    TB01007_NOME: equipment.emp_nome || firstEquipment?.emp_nome || '',
                                    TB01007_CNPJ: equipment.emp_cnpj || firstEquipment?.emp_cnpj || '',
                                    TB01007_ENDERECO: equipment.emp_endereco || firstEquipment?.emp_endereco || '',
                                    site_nome: equipment.site_nome || '',
                                    depto_nome: equipment.depto_nome || '',
                                    previsaoLabelOverride: previsaoLabelOverride || undefined
                                  };
                                });
                                await generateOSPdfV2FromOrderMultiple(orders, { openForPrint: true });
                              }
                            } finally {
                              setLoadingPdfPrint(null);
                              setShowOptionsDropdown(false);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            fontSize: '12px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            fontWeight: 500,
                            color: loadingPdfPrint ? 'var(--text-muted, #718096)' : 'var(--text-body, #2d3748)',
                            background: loadingPdfPrint === 'print' ? 'var(--border-subtle, #edf2f7)' : 'transparent',
                            border: 'none',
                            borderBottom: '1px solid var(--border-subtle, #e2e8f0)',
                            cursor: loadingPdfPrint ? 'wait' : 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => { if (!loadingPdfPrint) e.target.style.background = 'var(--bg-surface-alt, #f7fafc)'; }}
                          onMouseLeave={(e) => { e.target.style.background = loadingPdfPrint === 'print' ? 'var(--border-subtle, #edf2f7)' : 'transparent'; }}
                        >
                        {loadingPdfPrint === 'print' ? (
                          <>
                            <i className="bi bi-arrow-repeat order-impressao-spin" style={{ fontSize: '14px', color: '#7c4dff' }} aria-hidden />
                            Imprimindo…
                          </>
                        ) : (
                          <>
                            <i className="bi bi-printer" style={{ fontSize: '14px', color: '#A6A09B' }}></i>
                            Imprimir
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setShowOptionsDropdown(false);
                          setShowEmailModal(true);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          fontSize: '12px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          fontWeight: 500,
                          color: 'var(--text-body, #2d3748)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                        }}
                      >
                        <i className="bi bi-envelope" style={{ fontSize: '14px', color: '#A6A09B' }}></i>
                        Enviar email
                      </button>
                    </div>
                      </>
                  )}
                </div>
                  <button
                    onClick={() => {
                      setShowOSResultModal(false);
                      // Verificar se houve programação para atualizar a coluna correta
                      const hasScheduledOS = multipleOSResults.length > 0 && multipleOSResults[0]._scheduled;
                      const technicianId = multipleOSResults.length > 0 ? multipleOSResults[0]._technicianId : null;
                      const hasSuccess = multipleOSResults.some(r => r.success);
                      
                      if (hasSuccess && onOSCreated) {
                        onOSCreated({
                          scheduled: hasScheduledOS || false,
                          technicianId: technicianId || null
                        });
                      }
                      setMultipleOSResults([]);
                      handleClose();
                    }}
                    style={{
                      padding: '6px 16px',
                      fontSize: '12px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 600,
                      color: '#ffffff',
                      background: '#7c4dff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      boxShadow: '0 2px 6px rgba(124, 77, 255, 0.35)'
                    }}
                  >
                    Fechar
                  </button>
                </div>
              </>
            ) : osResult && osResult.success ? (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: 'auto',
                    height: 'auto',
                    minWidth: '32px',
                    minHeight: '32px',
                    borderRadius: '50%',
                    background: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    padding: '4px'
                  }}>
                    <i className="bi bi-check-circle-fill" style={{ fontSize: '16px', color: '#ffffff', lineHeight: 1 }}></i>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: 600,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)',
                      lineHeight: '1.4'
                    }}>
                      Ordem de Serviço criada com sucesso!
                    </h3>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap' }}>
                <div className="open-os-result-success" style={{
                  padding: '10px 12px',
                  background: '#f0fdf4',
                  borderRadius: '6px',
                  border: '1px solid #86efac',
                  display: 'inline-block',
                  width: 'auto',
                  minWidth: 'fit-content'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: '#166534',
                    marginBottom: '4px',
                    fontWeight: 500
                  }}>
                    Número da OS:
                  </div>
                  <div 
                    onClick={async () => {
                      if (osResult.osNumber) {
                        try {
                          // Função para copiar texto com fallback
                          const copyToClipboard = async (text) => {
                            // Tentar usar Clipboard API se disponível
                            if (navigator.clipboard && navigator.clipboard.writeText) {
                              try {
                                await navigator.clipboard.writeText(text);
                                return true;
                              } catch (err) {
                                // Se falhar, usar método de fallback
                                console.warn('Clipboard API falhou, usando método de fallback');
                              }
                            }
                            
                            // Método de fallback: criar elemento temporário
                            const textArea = document.createElement('textarea');
                            textArea.value = text;
                            textArea.style.position = 'fixed';
                            textArea.style.left = '-999999px';
                            textArea.style.top = '-999999px';
                            document.body.appendChild(textArea);
                            textArea.focus();
                            textArea.select();
                            
                            try {
                              const successful = document.execCommand('copy');
                              document.body.removeChild(textArea);
                              if (!successful) {
                                throw new Error('Falha ao copiar usando execCommand');
                              }
                              return true;
                            } catch (err) {
                              document.body.removeChild(textArea);
                              throw err;
                            }
                          };
                          
                          await copyToClipboard(osResult.osNumber);
                          setOsNumberCopied(true);
                          setTimeout(() => {
                            setOsNumberCopied(false);
                          }, 2000);
                        } catch (error) {
                          console.error('Erro ao copiar número da OS:', error);
                          // Opcional: mostrar mensagem de erro ao usuário
                          alert('Erro ao copiar número da OS. Por favor, copie manualmente: ' + osResult.osNumber);
                        }
                      }
                    }}
                    style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: osNumberCopied ? '#10b981' : '#166534',
                      lineHeight: '1.2',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease',
                      userSelect: 'none'
                    }}
                    title="Clique para copiar"
                    onMouseEnter={(e) => {
                      if (!osNumberCopied) {
                        e.target.style.opacity = '0.8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = '1';
                    }}
                  >
                    {osResult.osNumber || 'N/A'}
                    {osNumberCopied ? (
                      <i className="bi bi-check-circle-fill" style={{ fontSize: '14px', color: '#10b981' }}></i>
                    ) : (
                      <i className="bi bi-clipboard" style={{ fontSize: '12px', color: '#166534', opacity: 0.6 }}></i>
                    )}
                  </div>
                </div>
                {autoEmailResult && (
                  <div style={{
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${autoEmailResult.success ? '#86efac' : '#fecaca'}`,
                    background: autoEmailResult.success ? '#f0fdf4' : '#fef2f2',
                    /* class via wrapper */
                    display: 'inline-block',
                    width: 'auto',
                    minWidth: 'fit-content'
                  }}>
                    <div style={{
                      fontSize: '11px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: autoEmailResult.success ? '#166534' : '#991b1b',
                      marginBottom: '4px',
                      fontWeight: 500
                    }}>
                      {autoEmailResult.success ? 'Email enviado' : 'Erro no email'}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: autoEmailResult.success ? '#166534' : '#991b1b',
                      lineHeight: '1.2'
                    }}>
                      {autoEmailResult.message} → {autoEmailResult.to}
                    </div>
                  </div>
                )}
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  marginTop: '8px',
                  position: 'relative'
                }}>
                  {/* Dropdown Opções */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}
                      style={{
                        padding: '6px 16px',
                        fontSize: '12px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        fontWeight: 600,
                        color: '#ffffff',
                        background: '#5a67d8',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        width: 'auto',
                        minWidth: 'fit-content',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#4c51bf';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#5a67d8';
                      }}
                    >
                      <i className="bi bi-three-dots-vertical" style={{ fontSize: '14px' }}></i>
                      Opções
                    </button>
                    {showOptionsDropdown && (
                      <div 
                        data-options-dropdown
                        style={{
                          position: 'absolute',
                          bottom: '100%',
                          right: 0,
                          marginBottom: '8px',
                          background: 'var(--modal-bg, #ffffff)',
                          border: '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          minWidth: '180px',
                          zIndex: 1000,
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden'
                        }}
                      >
                        <button
                          type="button"
                          disabled={!!loadingPdfPrint}
                          onClick={async () => {
                            if (loadingPdfPrint) return;
                            setLoadingPdfPrint('pdf');
                            try {
                            if (selectedEquipment && osResult && osResult.osNumber) {
                                const dataFormatada = `${String(new Date().getDate()).padStart(2, '0')}/${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear()}`;
                                let previsaoLabelOverride = null;
                                if (scheduleNoCertainDate) previsaoLabelOverride = 'Não há data certa de previsão';
                                else if (scheduleData?.date) previsaoLabelOverride = formatDateToPTBR(scheduleData.date);
                                else if (scheduleType === 'tomorrow') previsaoLabelOverride = formatDateToPTBR(getNextBusinessDay());
                                else if (scheduleType === 'future' && selectedScheduleDate) previsaoLabelOverride = formatDateToPTBR(selectedScheduleDate);
                                const defeitoNome = selectedDefeito ? (selectedDefeito.nome || selectedDefeito.label || '') : '';
                                const orderSingle = {
                                  id: osResult.osNumber,
                                  cliente: selectedEquipment.cliente || (contractModeData?.contract?.classif) || '',
                                  endereco: selectedEquipment.enderecoCompleto || selectedEquipment.Endereco || '',
                                  TB02112_LOCAL: selectedEquipment.endereco?.local || selectedEquipment.Endereco_Local || '',
                                  equipamento: selectedEquipment.modelo || selectedEquipment.produto || '',
                                  numeroSerie: selectedEquipment.numSerie || selectedEquipment.serie || '',
                                  patrimonio: selectedEquipment.patrimonio || '',
                                  dataAbertura: dataFormatada,
                                  solicitante: solicitante || '',
                                  motivoOS: defeitoNome,
                                  motivoOSObservacao: defeitoObservacao || '',
                                  defeitoNome,
                                  defeitoObservacao: defeitoObservacao || '',
                                  tipo: osType === 'estoque' ? 'E' : (contractModeData?.tipoOs || 'C'),
                                  TB01007_NOME: selectedEquipment.emp_nome || '',
                                  TB01007_CNPJ: selectedEquipment.emp_cnpj || '',
                                  TB01007_ENDERECO: selectedEquipment.emp_endereco || '',
                                  site_nome: selectedEquipment.site_nome || '',
                                  depto_nome: selectedEquipment.depto_nome || '',
                                  previsaoLabelOverride: previsaoLabelOverride || undefined
                                };
                                await generateOSPdfV2Download(orderSingle);
                              }
                            } finally {
                              setLoadingPdfPrint(null);
                              setShowOptionsDropdown(false);
                            }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      fontSize: '12px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 500,
                      color: loadingPdfPrint ? 'var(--text-muted, #718096)' : 'var(--text-body, #2d3748)',
                      background: loadingPdfPrint === 'pdf' ? 'var(--border-subtle, #edf2f7)' : 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--border-subtle, #e2e8f0)',
                      cursor: loadingPdfPrint ? 'wait' : 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => { if (!loadingPdfPrint) e.target.style.background = 'var(--bg-surface-alt, #f7fafc)'; }}
                    onMouseLeave={(e) => { e.target.style.background = loadingPdfPrint === 'pdf' ? 'var(--border-subtle, #edf2f7)' : 'transparent'; }}
                  >
                    {loadingPdfPrint === 'pdf' ? (
                      <>
                        <i className="bi bi-arrow-repeat order-impressao-spin" style={{ fontSize: '14px', color: '#7c4dff' }} aria-hidden />
                        Gerando PDF…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-file-earmark-pdf" style={{ fontSize: '14px', color: '#A6A09B' }}></i>
                        Gerar PDF
                      </>
                    )}
                  </button>
                        <button
                          type="button"
                          disabled={!!loadingPdfPrint}
                          onClick={async () => {
                            if (loadingPdfPrint) return;
                            setLoadingPdfPrint('print');
                            try {
                            if (selectedEquipment && osResult && osResult.osNumber) {
                                const dataFormatadaPrint = `${String(new Date().getDate()).padStart(2, '0')}/${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear()}`;
                                let previsaoLabelOverridePrint = null;
                                if (scheduleNoCertainDate) previsaoLabelOverridePrint = 'Não há data certa de previsão';
                                else if (scheduleData?.date) previsaoLabelOverridePrint = formatDateToPTBR(scheduleData.date);
                                else if (scheduleType === 'tomorrow') previsaoLabelOverridePrint = formatDateToPTBR(getNextBusinessDay());
                                else if (scheduleType === 'future' && selectedScheduleDate) previsaoLabelOverridePrint = formatDateToPTBR(selectedScheduleDate);
                                const defeitoNomePrint = selectedDefeito ? (selectedDefeito.nome || selectedDefeito.label || '') : '';
                                const orderSinglePrint = {
                                  id: osResult.osNumber,
                                  cliente: selectedEquipment.cliente || (contractModeData?.contract?.classif) || '',
                                  endereco: selectedEquipment.enderecoCompleto || selectedEquipment.Endereco || '',
                                  TB02112_LOCAL: selectedEquipment.endereco?.local || selectedEquipment.Endereco_Local || '',
                                  equipamento: selectedEquipment.modelo || selectedEquipment.produto || '',
                                  numeroSerie: selectedEquipment.numSerie || selectedEquipment.serie || '',
                                  patrimonio: selectedEquipment.patrimonio || '',
                                  dataAbertura: dataFormatadaPrint,
                                  solicitante: solicitante || '',
                                  motivoOS: defeitoNomePrint,
                                  motivoOSObservacao: defeitoObservacao || '',
                                  defeitoNome: defeitoNomePrint,
                                  defeitoObservacao: defeitoObservacao || '',
                                  tipo: osType === 'estoque' ? 'E' : (contractModeData?.tipoOs || 'C'),
                                  TB01007_NOME: selectedEquipment.emp_nome || '',
                                  TB01007_CNPJ: selectedEquipment.emp_cnpj || '',
                                  TB01007_ENDERECO: selectedEquipment.emp_endereco || '',
                                  site_nome: selectedEquipment.site_nome || '',
                                  depto_nome: selectedEquipment.depto_nome || '',
                                  previsaoLabelOverride: previsaoLabelOverridePrint || undefined
                                };
                                await generateOSPdfV2FromOrder(orderSinglePrint, { openForPrint: true });
                              }
                            } finally {
                              setLoadingPdfPrint(null);
                              setShowOptionsDropdown(false);
                            }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      fontSize: '12px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 500,
                      color: loadingPdfPrint ? 'var(--text-muted, #718096)' : 'var(--text-body, #2d3748)',
                      background: loadingPdfPrint === 'print' ? 'var(--border-subtle, #edf2f7)' : 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--border-subtle, #e2e8f0)',
                      cursor: loadingPdfPrint ? 'wait' : 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => { if (!loadingPdfPrint) e.target.style.background = 'var(--bg-surface-alt, #f7fafc)'; }}
                    onMouseLeave={(e) => { e.target.style.background = loadingPdfPrint === 'print' ? 'var(--border-subtle, #edf2f7)' : 'transparent'; }}
                  >
                    {loadingPdfPrint === 'print' ? (
                      <>
                        <i className="bi bi-arrow-repeat order-impressao-spin" style={{ fontSize: '14px', color: '#7c4dff' }} aria-hidden />
                        Imprimindo…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-printer" style={{ fontSize: '14px', color: '#A6A09B' }}></i>
                        Imprimir
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowOptionsDropdown(false);
                      setShowEmailModal(true);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      fontSize: '12px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 500,
                      color: 'var(--text-body, #2d3748)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                    }}
                  >
                    <i className="bi bi-envelope" style={{ fontSize: '14px', color: '#A6A09B' }}></i>
                    Enviar email
                  </button>
                </div>
              )}
            </div>
                  <button
                    onClick={() => {
                      setShowOSResultModal(false);
                      setOsNumberCopied(false);
                      // Se a OS foi criada com sucesso, atualizar a coluna apropriada
                      if (osResult && osResult.success && onOSCreated) {
                        // Passar informações sobre a programação (já armazenadas no osResult)
                        onOSCreated({
                          scheduled: osResult.scheduled || false,
                          technicianId: osResult.technicianId || null
                        });
                      }
                      handleClose();
                    }}
                    style={{
                      padding: '6px 16px',
                      fontSize: '12px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 600,
                      color: '#ffffff',
                      background: '#7c4dff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      width: 'auto',
                      minWidth: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#5a3dc4';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#7c4dff';
                    }}
                  >
                    Fechar
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: 'auto',
                    height: 'auto',
                    minWidth: '32px',
                    minHeight: '32px',
                    borderRadius: '50%',
                    background: '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    padding: '4px'
                  }}>
                    <i className="bi bi-x-circle-fill" style={{ fontSize: '16px', color: '#ffffff', lineHeight: 1 }}></i>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: 600,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)',
                      lineHeight: '1.4'
                    }}>
                      Não foi possível gerar a OS
                    </h3>
                  </div>
                </div>
                <div style={{
                  padding: '16px',
                  background: '#fef2f2',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid #fecaca'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: '#991b1b',
                    marginBottom: '12px'
                  }}>
                    Ocorreu um erro ao criar a ordem de serviço. Por favor, tente novamente ou entre em contato com o administrador.
                  </div>
                  {osResult.errors && osResult.errors.length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowErrorDetails(!showErrorDetails)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          fontSize: '12px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          fontWeight: 500,
                          color: '#991b1b',
                          background: 'transparent',
                          border: '1px solid #fecaca',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#fee2e2';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                        }}
                      >
                        <i className={`bi bi-chevron-${showErrorDetails ? 'up' : 'down'}`} style={{ fontSize: '12px' }}></i>
                        <span>{showErrorDetails ? 'Ocultar' : 'Ver'} detalhes do erro ({osResult.errors.length})</span>
                      </button>
                      {showErrorDetails && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          background: 'var(--modal-bg, #ffffff)',
                          borderRadius: '6px',
                          border: '1px solid #fecaca',
                          maxHeight: '300px',
                          overflowY: 'auto'
                        }}>
                          {osResult.errors.map((error, index) => (
                            <div key={index} style={{
                              marginBottom: index < osResult.errors.length - 1 ? '12px' : 0,
                              paddingBottom: index < osResult.errors.length - 1 ? '12px' : 0,
                              borderBottom: index < osResult.errors.length - 1 ? '1px solid #fee2e2' : 'none'
                            }}>
                              {error.ErrorNumber && (
                                <div style={{ fontSize: '11px', color: '#991b1b', marginBottom: '4px' }}>
                                  <strong>Número:</strong> {error.ErrorNumber}
                                </div>
                              )}
                              {error.ErrorSeverity && (
                                <div style={{ fontSize: '11px', color: '#991b1b', marginBottom: '4px' }}>
                                  <strong>Severidade:</strong> {error.ErrorSeverity}
                                </div>
                              )}
                              {error.ErrorState && (
                                <div style={{ fontSize: '11px', color: '#991b1b', marginBottom: '4px' }}>
                                  <strong>Estado:</strong> {error.ErrorState}
                                </div>
                              )}
                              {error.ErrorProcedure && (
                                <div style={{ fontSize: '11px', color: '#991b1b', marginBottom: '4px' }}>
                                  <strong>Procedure:</strong> {error.ErrorProcedure}
                                </div>
                              )}
                              {error.ErrorLine && (
                                <div style={{ fontSize: '11px', color: '#991b1b', marginBottom: '4px' }}>
                                  <strong>Linha:</strong> {error.ErrorLine}
                                </div>
                              )}
                              {error.ErrorMessage && (
                                <div style={{ fontSize: '12px', color: '#991b1b', fontWeight: 500, marginTop: '4px' }}>
                                  <strong>Mensagem:</strong> {error.ErrorMessage}
                                </div>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={async () => {
                              const errorText = osResult.errors.map((error, index) => {
                                const parts = [];
                                if (error.ErrorNumber) parts.push(`Número: ${error.ErrorNumber}`);
                                if (error.ErrorSeverity) parts.push(`Severidade: ${error.ErrorSeverity}`);
                                if (error.ErrorState) parts.push(`Estado: ${error.ErrorState}`);
                                if (error.ErrorProcedure) parts.push(`Procedure: ${error.ErrorProcedure}`);
                                if (error.ErrorLine) parts.push(`Linha: ${error.ErrorLine}`);
                                if (error.ErrorMessage) parts.push(`Mensagem: ${error.ErrorMessage}`);
                                return `Erro ${index + 1}:\n${parts.join('\n')}`;
                              }).join('\n\n');
                              
                              // Função para copiar texto com fallback
                              const copyToClipboard = async (text) => {
                                // Tentar usar Clipboard API se disponível
                                if (navigator.clipboard && navigator.clipboard.writeText) {
                                  try {
                                    await navigator.clipboard.writeText(text);
                                    return true;
                                  } catch (err) {
                                    // Se falhar, usar método de fallback
                                    console.warn('Clipboard API falhou, usando método de fallback');
                                  }
                                }
                                
                                // Método de fallback: criar elemento temporário
                                const textArea = document.createElement('textarea');
                                textArea.value = text;
                                textArea.style.position = 'fixed';
                                textArea.style.left = '-999999px';
                                textArea.style.top = '-999999px';
                                document.body.appendChild(textArea);
                                textArea.focus();
                                textArea.select();
                                
                                try {
                                  const successful = document.execCommand('copy');
                                  document.body.removeChild(textArea);
                                  if (!successful) {
                                    throw new Error('Falha ao copiar usando execCommand');
                                  }
                                  return true;
                                } catch (err) {
                                  document.body.removeChild(textArea);
                                  throw err;
                                }
                              };
                              
                              try {
                                await copyToClipboard(errorText);
                                alert('Erros copiados para a área de transferência!');
                              } catch (error) {
                                console.error('Erro ao copiar erros:', error);
                                alert('Erro ao copiar erros. Por favor, copie manualmente.');
                              }
                            }}
                            style={{
                              marginTop: '12px',
                              width: '100%',
                              padding: '8px',
                              fontSize: '12px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              fontWeight: 500,
                              color: '#991b1b',
                              background: '#fee2e2',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#fecaca';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#fee2e2';
                            }}
                          >
                            <i className="bi bi-clipboard" style={{ marginRight: '6px' }}></i>
                            Copiar todos os erros
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '8px'
                }}>
                  <button
                    onClick={() => {
                      setShowOSResultModal(false);
                      setShowErrorDetails(false);
                      setOsNumberCopied(false);
                      // Não atualizar "Em aberto" em caso de erro
                      handleClose();
                    }}
                    style={{
                      padding: '6px 16px',
                      fontSize: '12px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 600,
                      color: '#ffffff',
                      background: '#ef4444',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      width: 'auto',
                      minWidth: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#dc2626';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#ef4444';
                    }}
                  >
                    Fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Modal de envio de email */}
      {showEmailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 20002,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          animation: emailSentSuccess ? 'fadeIn 0.3s ease-in-out' : 'none'
        }}
        onClick={() => {
          if (!isSendingEmail && !emailSentSuccess) {
            setShowEmailModal(false);
            setEmailSendError('');
            setEmailSentSuccess(false);
          }
        }}>
          <div style={{
            background: 'var(--modal-bg, #ffffff)',
            borderRadius: '12px',
            padding: emailSentSuccess ? '40px 24px' : '24px',
            maxWidth: emailSentSuccess ? '420px' : '500px',
            width: '100%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: emailSentSuccess ? 'scale(1.05)' : 'scale(1)',
            animation: emailSentSuccess ? 'successPulse 0.6s ease-in-out' : 'none'
          }}
          onClick={(e) => e.stopPropagation()}>
            {emailSentSuccess ? (
              // Mensagem de sucesso
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '24px',
                  animation: 'checkmarkAnimation 0.6s ease-in-out',
                  boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)'
                }}>
                  <i className="bi bi-check-circle-fill" style={{ 
                    fontSize: '48px', 
                    color: '#ffffff',
                    animation: 'scaleIn 0.5s ease-in-out'
                  }}></i>
                </div>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '20px',
                  fontWeight: 600,
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  color: '#1f2937'
                }}>
                  Email enviado com sucesso!
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  color: '#6b7280',
                  lineHeight: '1.5'
                }}>
                  O email foi enviado para <strong>{emailToSend}</strong>
                </p>
              </div>
            ) : (
              // Formulário de envio
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px'
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: 600,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-body, #2d3748)'
                  }}>
                    Enviar email
                  </h3>
                  <button
                    onClick={() => {
                      if (!isSendingEmail) {
                        setShowEmailModal(false);
                        setEmailSendError('');
                        setEmailSentSuccess(false);
                      }
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '20px',
                      color: 'var(--text-muted, #718096)',
                      cursor: isSendingEmail ? 'not-allowed' : 'pointer',
                      padding: '0',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: isSendingEmail ? 0.5 : 1
                    }}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <input
                    type="email"
                    value={emailToSend}
                    onChange={(e) => {
                      setEmailToSend(e.target.value);
                      setEmailSendError('');
                    }}
                    placeholder="Digite o email do destinatário"
                    disabled={isSendingEmail}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      fontSize: '13px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      border: emailSendError ? '2px solid #ef4444' : '2px solid var(--border-subtle, #e2e8f0)',
                      borderRadius: '8px',
                      outline: 'none',
                      transition: 'border 0.2s',
                      backgroundColor: isSendingEmail ? 'var(--bg-surface-alt, #f7fafc)' : '#ffffff'
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !isSendingEmail && validateEmail(emailToSend)) {
                        handleSendEmail();
                      }
                    }}
                  />
                  <button
                    onClick={handleSendEmail}
                    disabled={!validateEmail(emailToSend) || isSendingEmail}
                    style={{
                      padding: '10px 20px',
                      fontSize: '13px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 600,
                      color: '#ffffff',
                      background: (!validateEmail(emailToSend) || isSendingEmail) ? '#cbd5e0' : '#7c4dff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (!validateEmail(emailToSend) || isSendingEmail) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (validateEmail(emailToSend) && !isSendingEmail) {
                        e.target.style.background = '#5a3dc4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (validateEmail(emailToSend) && !isSendingEmail) {
                        e.target.style.background = '#7c4dff';
                      }
                    }}
                  >
                    {isSendingEmail ? (
                      <>
                        <i className="bi bi-arrow-repeat" style={{ fontSize: '14px', animation: 'spin 1s linear infinite' }}></i>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send" style={{ fontSize: '14px' }}></i>
                        Enviar
                      </>
                    )}
                  </button>
                </div>
                
                {emailSendError && (
                  <div style={{
                    padding: '12px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: '#991b1b'
                    }}>
                      {emailSendError}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes successPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        @keyframes checkmarkAnimation {
          0% { 
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% { 
            transform: scale(1);
            opacity: 1;
          }
        }
        @keyframes scaleIn {
          0% { 
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% { 
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default OpenOSModal;

